/**
 * Character distribution data for a language
 */
export interface LanguageDistribution {
  trigrams: Map<string, number>;  // Top trigrams with normalized frequency
  uniqueChars: Set<string>;       // Characters distinctive to this language
  scriptPrimary: RegExp;          // Primary script regex for quick filtering
  scriptSecondary?: RegExp;       // Optional secondary script for mixed-script languages
}

/**
 * Class to manage language distributions
 * This approach prevents minification from mangling language property names
 */
export class LanguageDistributions {
  private static _distributions = new Map<string, LanguageDistribution>();

  /**
   * Initialize all language distributions
   * Called once at startup
   */
  static initialize(): void {
    this._distributions.set('afrikaans', {
      trigrams: new Map([
        ['die', 0.0342], ['en ', 0.0215], ['aan', 0.0189], ['van', 0.0183],
        ['eer', 0.0172], ['ing', 0.0167], ['nie', 0.0158], ['der', 0.0146],
        ['oor', 0.0132], ['dat', 0.0129], ['n d', 0.0122], ['te ', 0.0117],
        ['is ', 0.0113], ['ver', 0.0108], ['nde', 0.0104], ['het', 0.0097],
        ['and', 0.0093], ['ons', 0.0089], ['met', 0.0085], ['wat', 0.0081],
        ['ik ', 0.0078], ['vir', 0.0073], ['ste', 0.0069], ['in ', 0.0066],
        ['end', 0.0063], ['ers', 0.0059], ['as ', 0.0056], ['aar', 0.0053],
        ['ie ', 0.0050], ['op ', 0.0047]
      ]),
      uniqueChars: new Set(['ê', 'ë', 'ô', 'û']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('albanian', {
      trigrams: new Map([
        ['të ', 0.0289], ['dhe', 0.0212], ['së ', 0.0189], ['ë t', 0.0176],
        ['në ', 0.0165], ['për', 0.0158], ['ën ', 0.0143], ['me ', 0.0137],
        ['et ', 0.0128], ['shë', 0.0122], ['ht ', 0.0116], ['e t', 0.0112],
        ['ar ', 0.0107], ['in ', 0.0103], ['je ', 0.0099], ['it ', 0.0095],
        ['ve ', 0.0092], ['jë ', 0.0089], ['re ', 0.0086], ['ta ', 0.0083],
        ['at ', 0.0080], ['he ', 0.0077], ['nga', 0.0074], ['uar', 0.0071],
        ['ka ', 0.0068], ['shi', 0.0065], ['ëri', 0.0062], ['ish', 0.0059],
        ['htë', 0.0056], ['si ', 0.0053]
      ]),
      uniqueChars: new Set(['ë', 'ç', 'ù']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('amharic', {
      trigrams: new Map([
        ['ነው', 0.0187], ['የተ', 0.0176], ['ም፣', 0.0168], ['ይህ', 0.0159],
        ['ና ', 0.0148], ['ውን', 0.0143], ['ስለ', 0.0137], ['ብቻ', 0.0129],
        ['ሆነ', 0.0124], ['ላይ', 0.0118], ['እና', 0.0114], ['ከዚ', 0.0110],
        ['ዚህ', 0.0106], ['ን፣', 0.0102], ['ከዚ', 0.0098], ['ወይ', 0.0094],
        ['ይም', 0.0090], ['አለ', 0.0086], ['ዎች', 0.0082], ['ም፤', 0.0078],
        ['አድ', 0.0074], ['ድር', 0.0070], ['ርጎ', 0.0066], ['ገሰ', 0.0062],
        ['ሰኞ', 0.0058], ['በተ', 0.0054], ['ተለ', 0.0050], ['ለያ', 0.0046],
        ['ያዩ', 0.0042], ['ዩት', 0.0038]
      ]),
      uniqueChars: new Set(['ሀ', 'ለ', 'ሐ', 'መ', 'ሠ', 'ረ', 'ሰ', 'ሸ', 'ቀ', 'በ']),
      scriptPrimary: /[\u1200-\u137F]/g
    });

    // this._distributions.set('arabic', {
    //   trigrams: new Map([
    //     ['ال', 0.0324], ['في', 0.0202], ['من', 0.0183], ['على', 0.0162],
    //     ['هذا', 0.0151], ['أن', 0.0142], ['لا', 0.0138], ['ما', 0.0129],
    //     ['إلى', 0.0127], ['ان', 0.0123], ['ين', 0.0119], ['ية', 0.0115],
    //     ['ها', 0.0112], ['يا', 0.0109], ['هي', 0.0106], ['ات', 0.0103],
    //     ['لي', 0.0100], ['ون', 0.0097], ['ني', 0.0094], ['وا', 0.0091],
    //     ['م ا', 0.0088], ['ه ا', 0.0085], ['ا ا', 0.0082], ['ا ل', 0.0079],
    //     ['ل ا', 0.0076], ['كان', 0.0073], ['ذلك', 0.0070], ['لك', 0.0067],
    //     ['له', 0.0064], ['لم', 0.0061]
    //   ]),
    //   uniqueChars: new Set(['ء', 'ئ', 'ؤ', 'ة', 'ى', 'آ', 'إ', 'أ', 'ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج']),
    //   scriptPrimary: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g
    // });

    this._distributions.set('arabic', {
      trigrams: new Map([
        // Common MSA (Modern Standard Arabic) trigrams
        ['ال', 0.0324], ['في', 0.0202], ['من', 0.0183], ['على', 0.0162],
        ['هذا', 0.0151], ['أن', 0.0142], ['لا', 0.0138], ['ما', 0.0129],
        ['إلى', 0.0127], ['ان', 0.0123], ['ين', 0.0119], ['ية', 0.0115],
        ['ها', 0.0112], ['يا', 0.0109], ['هي', 0.0106], ['ات', 0.0103],
        ['لي', 0.0100], ['ون', 0.0097], ['ني', 0.0094], ['وا', 0.0091],
        ['م ا', 0.0088], ['ه ا', 0.0085], ['ا ا', 0.0082], ['ا ل', 0.0079],
        ['ل ا', 0.0076], ['كان', 0.0073], ['ذلك', 0.0070], ['لك', 0.0067],
        ['له', 0.0064], ['لم', 0.0061],

        // Additional distinctive MSA Arabic trigrams
        ['ذا ', 0.0059], ['ني ', 0.0057], ['سل', 0.0055], ['ام ', 0.0053],
        ['الع', 0.0051], ['عرب', 0.0049], ['ربي', 0.0047], ['بية', 0.0045],
        ['لعر', 0.0043], ['مصر', 0.0041], ['تي ', 0.0039], ['عن ', 0.0037],

        // Distinctively Arabic (vs Persian/Dari) digrams/trigrams
        ['ض', 0.0035], ['ص', 0.0033], ['ث', 0.0031], ['ذ', 0.0029],
        ['ظ', 0.0027], ['ط', 0.0025], ['ة', 0.0023], ['أ', 0.0021],
        ['ضي', 0.0019], ['صر', 0.0017], ['ثم', 0.0015], ['ذه', 0.0013],
        ['ظر', 0.0011], ['طب', 0.0009], ['ة ', 0.0007], ['أن', 0.0005]
      ]),
      uniqueChars: new Set([
        // Distinctively Arabic letters not common in Persian/Dari
        'ء', 'ئ', 'ؤ', 'ة', 'ى', 'آ', 'إ', 'أ', 'ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج', 'ط', 'ظ', 'ذ',
        // Tashkeel (diacritics) - very distinctive for Arabic
        'َ', 'ُ', 'ِ', 'ّ', 'ْ', 'ً', 'ٌ', 'ٍ'
      ]),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g
    });

    this._distributions.set('armenian', {
      trigrams: new Map([
        ['ուն', 0.0213], ['երի', 0.0197], ['ան ', 0.0189], ['ներ', 0.0178],
        ['ի վ', 0.0169], ['րի ', 0.0158], ['ը ', 0.0149], ['յան', 0.0138],
        ['ամ ', 0.0127], ['են ', 0.0119], ['նե ', 0.0112], ['որ ', 0.0106],
        ['նի ', 0.0101], ['եր ', 0.0096], ['ար ', 0.0092], ['ին ', 0.0088],
        ['րա ', 0.0084], ['վա ', 0.0080], ['այ ', 0.0076], ['ւմ ', 0.0072],
        ['ակ ', 0.0068], ['տա ', 0.0064], ['ալ ', 0.0060], ['ատ ', 0.0056],
        ['ած ', 0.0052], ['իկ ', 0.0048], ['ով ', 0.0044], ['պա ', 0.0040],
        ['տի ', 0.0036], ['ող ', 0.0032]
      ]),
      uniqueChars: new Set(['փ', 'ջ', 'օ', 'ռ', 'ժ', 'ձ', 'ճ', 'մ', 'յ', 'ն', 'շ', 'ղ', 'ճ', 'ց', 'ւ', 'և']),
      scriptPrimary: /[\u0530-\u058F]/g
    });

    this._distributions.set('assamese', {
      trigrams: new Map([
        ['কৰ', 0.0213], ['ৰ ', 0.0198], ['ৰে', 0.0187], ['াৰ', 0.0176],
        ['ক্ষ', 0.0165], ['ত ', 0.0154], ['নে ', 0.0143], ['পৰ', 0.0132],
        ['য়া', 0.0121], ['লো', 0.0110], ['অস', 0.0106], ['সম', 0.0102],
        ['ময়', 0.0098], ['য়া', 0.0094], ['ৰ দ', 0.0090], ['দে', 0.0086],
        ['েশ', 0.0082], ['শৰ', 0.0078], ['ৰ প', 0.0074], ['প্', 0.0070],
        ['্ৰ', 0.0066], ['ৰত', 0.0062], ['তি', 0.0058], ['িত', 0.0054],
        ['ৰ ক', 0.0050], ['কা', 0.0046], ['াম', 0.0042], ['মৰ', 0.0038],
        ['ৰু', 0.0034], ['ুপ', 0.0030]
      ]),
      uniqueChars: new Set(['অ', 'আ', 'ই', 'ঈ', 'উ', 'ঊ', 'ঋ', 'এ', 'ঐ', 'ও', 'ঔ', 'ক', 'খ', 'গ', 'ঘ', 'ঙ', 'চ', 'ছ', 'জ', 'ঝ', 'ঞ', 'ট', 'ঠ', 'ড', 'ঢ', 'ণ', 'ত', 'থ', 'দ', 'ধ', 'ন', 'প', 'ফ', 'ব', 'ভ', 'ম', 'য', 'ৰ', 'ল', 'শ', 'ষ', 'স', 'হ', 'ড়', 'ঢ়', 'য়', 'ৎ']),
      scriptPrimary: /[\u0980-\u09FF]/g
    });
// azeri: {
    //   trigrams: new Map([
    //     ['lar', 0.0187], ['in ', 0.0178], ['ən ', 0.0169], ['də ', 0.0159],
    //     ['da ', 0.0149], ['ir ', 0.0139], ['an ', 0.0129], ['və ', 0.0119],
    //     ['dir', 0.0109], ['ını', 0.0099], ['ar ', 0.0095], ['ər ', 0.0091],
    //     ['nın', 0.0087], ['ın ', 0.0083], ['il ', 0.0079], ['ilə', 0.0075],
    //     ['aq ', 0.0071], ['rın', 0.0067], ['ara', 0.0063], ['ası', 0.0059],
    //     ['əri', 0.0055], ['na ', 0.0051], ['nda', 0.0047], ['iya', 0.0043],
    //     ['yan', 0.0039], ['dır', 0.0035], ['ki ', 0.0031], ['ind', 0.0027],
    //     ['ndə', 0.0023], ['ola', 0.0019]
    //   ]),
    //   uniqueChars: new Set(['ə', 'ı', 'ö', 'ü', 'ğ', 'ç', 'ş']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g,
    //   scriptSecondary: /[\u0400-\u04FF]/g // For Cyrillic variant
    // },
    // this._distributions.set('azeri_cyrillic', {
    //   trigrams: new Map([
    //     ['лар', 0.0187], ['ин ', 0.0178], ['ән ', 0.0169], ['дә ', 0.0159],
    //     ['да ', 0.0149], ['ир ', 0.0139], ['ан ', 0.0129], ['вә ', 0.0119],
    //     ['дир', 0.0109], ['ыны', 0.0099], ['ар ', 0.0095], ['әр ', 0.0091],
    //     ['нын', 0.0087], ['ын ', 0.0083], ['ил ', 0.0079], ['илә', 0.0075],
    //     ['аг ', 0.0071], ['рын', 0.0067], ['ара', 0.0063], ['асы', 0.0059],
    //     ['әри', 0.0055], ['на ', 0.0051], ['нда', 0.0047], ['ийа', 0.0043],
    //     ['йан', 0.0039], ['дыр', 0.0035], ['ки ', 0.0031], ['инд', 0.0027],
    //     ['ндә', 0.0023], ['ола', 0.0019]
    //   ]),
    //   uniqueChars: new Set(['ә', 'ы', 'ө', 'ү', 'ғ', 'ч', 'ш']),
    //   scriptPrimary: /[\u0400-\u04FF]/g
    // });

    this._distributions.set('azerbaijani_cyrillic', {
      trigrams: new Map([
        // Common Azerbaijani Cyrillic trigrams
        ['лар', 0.0187], ['ин ', 0.0178], ['ән ', 0.0169], ['дә ', 0.0159],
        ['да ', 0.0149], ['ир ', 0.0139], ['ан ', 0.0129], ['вә ', 0.0119],
        ['дир', 0.0109], ['ыны', 0.0099], ['ар ', 0.0095], ['әр ', 0.0091],
        ['нын', 0.0087], ['ын ', 0.0083], ['ил ', 0.0079], ['илә', 0.0075],
        ['аг ', 0.0071], ['рын', 0.0067], ['ара', 0.0063], ['асы', 0.0059],
        ['әри', 0.0055], ['на ', 0.0051], ['нда', 0.0047], ['ийа', 0.0043],
        ['йан', 0.0039], ['дыр', 0.0035], ['ки ', 0.0031], ['инд', 0.0027],
        ['ндә', 0.0023], ['ола', 0.0019],

        // Distinctive Azerbaijani Cyrillic trigrams
        ['азә', 0.0044], ['зәр', 0.0042], ['әрб', 0.0040], ['рба', 0.0038],
        ['бај', 0.0036], ['ајҹ', 0.0034], ['јҹа', 0.0032], ['ҹан', 0.0030],
        ['ан ', 0.0028], ['н р', 0.0026], ['рес', 0.0024], ['есп', 0.0022],
        ['спу', 0.0020], ['пуб', 0.0018], ['убл', 0.0016], ['бли', 0.0014],

        // Azerbaijani-specific patterns with distinctive letters
        ['ҝ г', 0.0012], ['гыз', 0.0010], ['ыз ', 0.0008], ['з д', 0.0006],
        ['дә', 0.0005], ['ә ҹ', 0.0004], ['ҹән', 0.0003], ['әну', 0.0002],
        ['нуб', 0.0001], ['б г', 0.00009], ['гаф', 0.00008], ['фга', 0.00007]
      ]),
      uniqueChars: new Set([
        // Distinctive Azerbaijani Cyrillic characters
        'ә', 'ы', 'ө', 'ү', 'ғ', 'ҝ', 'ҹ', 'ҽ', 'Ә', 'Ы', 'Ө', 'Ү', 'Ғ', 'Ҝ', 'Ҹ', 'Ҽ',
        // Other Cyrillic letters that help differentiate
        'ь', 'ъ'
      ]),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('azerbaijani_latin', {  trigrams: new Map([
        ['lar', 0.0213], ['ar ', 0.0197], ['da ', 0.0187], ['in ', 0.0176],
        ['ir ', 0.0165], ['an ', 0.0154], ['də ', 0.0143], ['dir', 0.0132],
        ['ən ', 0.0121], ['lər', 0.0110], ['nın', 0.0105], ['nin', 0.0100],
        ['və ', 0.0096], ['ini', 0.0092], ['ı v', 0.0088], ['eri', 0.0084],
        ['arı', 0.0080], ['ilə', 0.0076], ['ını', 0.0072], ['əri', 0.0068],
        ['dan', 0.0064], ['ndə', 0.0060], ['dən', 0.0056], ['iya', 0.0052],
        ['ınd', 0.0048], ['ası', 0.0044], ['nda', 0.0040], ['ə b', 0.0036],
        ['ana', 0.0032], ['ind', 0.0028], ['n b', 0.0027], ['bir', 0.0026],
        ['əni', 0.0025], ['aya', 0.0024], ['şı ', 0.0023], ['ğı ', 0.0022],
        ['li ', 0.0021], ['ki ', 0.0020], ['ır ', 0.0019], ['dır', 0.0018],
        ['ək ', 0.0017], ['etm', 0.0016], ['n v', 0.0015], ['ard', 0.0014],
        ['əri', 0.0013], ['əsi', 0.0012], ['yev', 0.0011], ['bak', 0.0010],
        ['azə', 0.0009], ['rba', 0.0008], ['ayc', 0.0007], ['can', 0.0006],
        ['zər', 0.0005], ['bay', 0.0004], ['ycə', 0.0003], ['ölk', 0.0002],
        ['çün', 0.0001], ['ğun', 0.00009], ['öz ', 0.00008], ['şəh', 0.00007],
        ['ümk', 0.00006], ['kən', 0.00005], ['xal', 0.00004], ['həm', 0.00003],
        ['çıx', 0.00002], ['ğım', 0.00001], ['öyü', 0.000009], ['ümə', 0.000008],
        ['xey', 0.000007], ['şıq', 0.000006], ['ğa ', 0.000005], ['üçü', 0.000004],
        ['azərb', 0.000003], ['baycə', 0.000002], ['azərbay', 0.000001]]),
      uniqueChars: new Set(['ə', 'ç', 'ğ', 'ı', 'İ', 'ö', 'ş', 'ü', 'Ə', 'Ç', 'Ğ', 'I', 'Ö', 'Ş', 'Ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    // this._distributions.set('azeri_latin', {
    //   trigrams: new Map([
    //     ['lar', 0.0187], ['in ', 0.0178], ['ən ', 0.0169], ['də ', 0.0159],
    //     ['da ', 0.0149], ['ir ', 0.0139], ['an ', 0.0129], ['və ', 0.0119],
    //     ['dir', 0.0109], ['ını', 0.0099], ['ar ', 0.0095], ['ər ', 0.0091],
    //     ['nın', 0.0087], ['ın ', 0.0083], ['il ', 0.0079], ['ilə', 0.0075],
    //     ['aq ', 0.0071], ['rın', 0.0067], ['ara', 0.0063], ['ası', 0.0059],
    //     ['əri', 0.0055], ['na ', 0.0051], ['nda', 0.0047], ['iya', 0.0043],
    //     ['yan', 0.0039], ['dır', 0.0035], ['ki ', 0.0031], ['ind', 0.0027],
    //     ['ndə', 0.0023], ['ola', 0.0019]
    //   ]),
    //   uniqueChars: new Set(['ə', 'ı', 'ö', 'ü', 'ğ', 'ç', 'ş']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // });

    this._distributions.set('belarusian', {
      trigrams: new Map([
        ['ага', 0.0213], ['ны ', 0.0197], ['на ', 0.0187], ['ць ', 0.0176],
        ['ны ', 0.0165], ['ай ', 0.0154], ['ць ', 0.0143], ['ва ', 0.0132],
        ['ыя ', 0.0121], ['анн', 0.0110], ['ння', 0.0106], ['ла ', 0.0102],
        ['аць', 0.0098], ['ста', 0.0094], ['і п', 0.0090], ['пра', 0.0086],
        ['га ', 0.0082], ['ара', 0.0078], ['рац', 0.0074], ['ацы', 0.0070],
        ['цыя', 0.0066], ['аго', 0.0062], ['гэт', 0.0058], ['эта', 0.0054],
        ['так', 0.0050], ['акс', 0.0046], ['кса', 0.0042], ['сам', 0.0038],
        ['ама', 0.0034], ['мат', 0.0030]
      ]),
      uniqueChars: new Set(['ў', 'і', 'ь', 'э', 'ы', 'ё', 'ж', 'ц', 'ч', 'ш', 'щ']),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('bengali', {
      trigrams: new Map([
        ['এর', 0.0215], ['কর', 0.0187], ['রে', 0.0179], ['ার', 0.0166],
        ['দে', 0.0156], ['না', 0.0149], ['লে', 0.0142], ['বা', 0.0135],
        ['তা', 0.0128], ['িত', 0.0123], ['রা', 0.0118], ['ের', 0.0113],
        ['ান', 0.0108], ['ির', 0.0103], ['কে', 0.0098], ['ায়', 0.0093],
        ['তি', 0.0088], ['করে', 0.0083], ['েরে', 0.0078], ['রের', 0.0073],
        ['জন', 0.0068], ['ন্য', 0.0063], ['য্য', 0.0058], ['য্যে', 0.0053],
        ['েন', 0.0048], ['নিয়', 0.0043], ['িয়ে', 0.0038], ['য়ে', 0.0033],
        ['ে প', 0.0028], ['পর', 0.0023]
      ]),
      uniqueChars: new Set(['অ', 'আ', 'ই', 'ঈ', 'উ', 'ঊ', 'ঋ', 'এ', 'ঐ', 'ও', 'ঔ', 'ক', 'খ', 'গ', 'ঘ', 'ঙ', 'চ', 'ছ', 'জ', 'ঝ', 'ঞ', 'ট', 'ঠ', 'ড', 'ঢ', 'ণ', 'ত', 'থ', 'দ', 'ধ', 'ন', 'প', 'ফ', 'ব', 'ভ', 'ম', 'য', 'র', 'ল', 'শ', 'ষ', 'স', 'হ', 'ড়', 'ঢ়', 'য়', 'ৎ']),
      scriptPrimary: /[\u0980-\u09FF]/g
    });

    this._distributions.set('bosnian', {
      trigrams: new Map([
        ['ije', 0.0176], ['ost', 0.0168], ['je ', 0.0159], ['na ', 0.0149],
        ['ti ', 0.0139], ['nje', 0.0129], ['da ', 0.0119], ['ko ', 0.0109],
        ['ati', 0.0099], ['koj', 0.0089], ['sam', 0.0086], ['mo ', 0.0083],
        ['jed', 0.0080], ['va ', 0.0077], ['a s', 0.0074], ['e s', 0.0071],
        ['i n', 0.0068], ['za ', 0.0065], ['iti', 0.0062], ['eni', 0.0059],
        ['u s', 0.0056], ['ov ', 0.0053], ['ovi', 0.0050], ['ih ', 0.0047],
        ['ju ', 0.0044], ['li ', 0.0041], ['sta', 0.0038], ['ova', 0.0035],
        ['pre', 0.0032], ['ima', 0.0029]
      ]),
      uniqueChars: new Set(['č', 'ć', 'đ', 'š', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g,
      scriptSecondary: /[\u0400-\u04FF]/g // For Cyrillic variant
    });

    this._distributions.set('bulgarian', {
      trigrams: new Map([
        ['на ', 0.0289], ['то ', 0.0245], ['та ', 0.0223], ['ни ', 0.0197],
        ['ат ', 0.0187], ['да ', 0.0176], ['и п', 0.0165], ['е н', 0.0154],
        ['о н', 0.0143], ['а с', 0.0132], ['ите', 0.0128], ['ите', 0.0124],
        ['ят ', 0.0120], ['от ', 0.0116], ['се ', 0.0112], ['за ', 0.0108],
        ['го ', 0.0104], ['ко ', 0.0100], ['а п', 0.0096], ['пре', 0.0092],
        ['ре ', 0.0088], ['ани', 0.0084], ['ния', 0.0080], ['но ', 0.0076],
        ['ва ', 0.0072], ['ест', 0.0068], ['ств', 0.0064], ['тво', 0.0060],
        ['ват', 0.0056], ['ото', 0.0052]
      ]),
      uniqueChars: new Set(['ъ', 'ь', 'щ', 'я', 'ю', 'ж', 'ц', 'ч', 'ш']), // Distinctive Bulgarian characters
      scriptPrimary: /[\u0400-\u04FF]/g // Cyrillic script
    });

    this._distributions.set('burmese', {
      trigrams: new Map([
        ['မျာ', 0.0213], ['နေ', 0.0197], ['တာ', 0.0187], ['သည်', 0.0176],
        ['ကို', 0.0165], ['ရန်', 0.0154], ['တွင်', 0.0143], ['ဖြစ်', 0.0132],
        ['များ', 0.0121], ['သော', 0.0110], ['င်း', 0.0107], ['ခြင', 0.0104],
        ['င်း', 0.0101], ['းသည', 0.0098], ['ည်', 0.0095], ['လျှ', 0.0092],
        ['ျှင်', 0.0089], ['င်', 0.0086], ['သူ', 0.0083], ['ူမျ', 0.0080],
        ['ျား', 0.0077], ['းက', 0.0074], ['ကြ', 0.0071], ['ြသ', 0.0068],
        ['သည်', 0.0065], ['ည်။', 0.0062], ['။', 0.0059], ['ရှိ', 0.0056],
        ['ှိသ', 0.0053], ['သည်', 0.0050]
      ]),
      uniqueChars: new Set(['က', 'ခ', 'ဂ', 'ဃ', 'င', 'စ', 'ဆ', 'ဇ', 'ဈ', 'ည', 'ဋ', 'ဌ', 'ဍ', 'ဎ', 'ဏ', 'တ', 'ထ', 'ဒ', 'ဓ', 'န', 'ပ', 'ဖ', 'ဗ', 'ဘ', 'မ', 'ယ', 'ရ', 'လ', 'ဝ', 'သ', 'ဟ', 'ဠ', 'အ']),
      scriptPrimary: /[\u1000-\u109F]/g
    });

    // this._distributions.set('catalan', {
    //   trigrams: new Map([
    //     ['de ', 0.0213], ['es ', 0.0197], ['la ', 0.0187], ['el ', 0.0176],
    //     ['ent', 0.0165], ['que', 0.0154], ['ció', 0.0143], ['ts ', 0.0132],
    //     ['s d', 0.0121], ['a d', 0.0110], ['nt ', 0.0105], ['per', 0.0100],
    //     ['ls ', 0.0096], ['al ', 0.0092], ['tat', 0.0088], ['amb', 0.0084],
    //     ['una', 0.0080], ['est', 0.0076], ['ues', 0.0072], ['s p', 0.0068],
    //     ['s e', 0.0064], ['ns ', 0.0060], ['me ', 0.0056], ['en ', 0.0052],
    //     ['rs ', 0.0048], ['del', 0.0044], ['i d', 0.0040], ['no ', 0.0036],
    //     ['com', 0.0032], ['tre', 0.0028]
    //   ]),
    //   uniqueChars: new Set(['à', 'ç', 'é', 'è', 'í', 'ï', 'ó', 'ò', 'ú', 'ü']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    // });

    this._distributions.set('catalan', {
      trigrams: new Map([
        // Core Catalan trigrams with adjusted frequencies
        ['de ', 0.0213], ['es ', 0.0197], ['la ', 0.0187], ['el ', 0.0176],
        ['ent', 0.0165], ['que', 0.0154], ['ció', 0.0143], ['ts ', 0.0132],
        ['s d', 0.0121], ['a d', 0.0110], ['nt ', 0.0105], ['per', 0.0100],
        ['ls ', 0.0096], ['al ', 0.0092], ['tat', 0.0088], ['amb', 0.0084],
        ['una', 0.0080], ['est', 0.0076], ['ues', 0.0072], ['s p', 0.0068],
        ['s e', 0.0064], ['ns ', 0.0060], ['me ', 0.0056], ['en ', 0.0052],
        ['rs ', 0.0048], ['del', 0.0044], ['i d', 0.0040], ['no ', 0.0036],
        ['com', 0.0032], ['tre', 0.0028],

        // Adding highly distinctive Catalan patterns with increased frequencies
        ['nyj', 0.0075], ['ny ', 0.0072], ['ç', 0.0070], ['ons', 0.0068],
        ['eix', 0.0065], ['ix ', 0.0062], ['ix', 0.0060], ['l·l', 0.0058],
        ['·l', 0.0055], ['ès ', 0.0052], ['ès', 0.0050], ['ül', 0.0048],
        ['à ', 0.0045], ['è ', 0.0043], ['í ', 0.0041], ['ò ', 0.0039],

        // Additional distinctive Catalan trigrams
        ['aix', 0.0037], ['uix', 0.0035], ['l·l', 0.0033], ['gui', 0.0031],
        ['qü', 0.0029], ['ïn', 0.0027], ['ïn', 0.0025], ['tz', 0.0023],
        ['tll', 0.0021], ['tj', 0.0019], ['tg', 0.0017], ['lls', 0.0015],
        ['ny', 0.0013], ['tx', 0.0011], ['ig ', 0.0009], ['ot', 0.0007]
      ]),
      uniqueChars: new Set([
        'à', 'ç', 'é', 'è', 'í', 'ï', 'ó', 'ò', 'ú', 'ü', '·',
        'À', 'Ç', 'É', 'È', 'Í', 'Ï', 'Ó', 'Ò', 'Ú', 'Ü', '·'
      ]),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

// chinese: {
    //   trigrams: new Map([
    //     ['的', 0.0421], ['一', 0.0230], ['是', 0.0210], ['不', 0.0181],
    //     ['了', 0.0142], ['在', 0.0135], ['人', 0.0131], ['有', 0.0122],
    //     ['我', 0.0120], ['他', 0.0117], ['这', 0.0114], ['个', 0.0110],
    //     ['们', 0.0107], ['中', 0.0104], ['来', 0.0101], ['上', 0.0098],
    //     ['大', 0.0095], ['和', 0.0092], ['国', 0.0089], ['地', 0.0086],
    //     ['到', 0.0083], ['以', 0.0080], ['说', 0.0077], ['时', 0.0074],
    //     ['要', 0.0071], ['就', 0.0068], ['出', 0.0065], ['会', 0.0062],
    //     ['可', 0.0059], ['也', 0.0056]
    //   ]),
    //   uniqueChars: new Set(['们', '个', '为', '这', '那', '国', '华', '是', '的', '了']),
    //   scriptPrimary: /[\u4E00-\u9FFF\u3400-\u4DBF]/g
    // },
    this._distributions.set('chinese_simplified', {
      trigrams: new Map([
        ['的', 0.0421], ['一', 0.0230], ['是', 0.0210], ['不', 0.0181],
        ['了', 0.0142], ['在', 0.0135], ['人', 0.0131], ['有', 0.0122],
        ['我', 0.0120], ['他', 0.0117], ['这', 0.0114], ['个', 0.0110],
        ['们', 0.0107], ['中', 0.0104], ['来', 0.0101], ['上', 0.0098],
        ['大', 0.0095], ['和', 0.0092], ['国', 0.0089], ['地', 0.0086],
        ['到', 0.0083], ['以', 0.0080], ['说', 0.0077], ['时', 0.0074],
        ['要', 0.0071], ['就', 0.0068], ['出', 0.0065], ['会', 0.0062],
        ['可', 0.0059], ['也', 0.0056]
      ]),
      uniqueChars: new Set(['们', '个', '为', '这', '那', '国', '华', '是', '的', '了']),
      scriptPrimary: /[\u4E00-\u9FFF\u3400-\u4DBF]/g
    });

    this._distributions.set('chinese_traditional', {
      trigrams: new Map([
        ['的', 0.0421], ['一', 0.0230], ['是', 0.0210], ['不', 0.0181],
        ['了', 0.0142], ['在', 0.0135], ['人', 0.0131], ['有', 0.0122],
        ['我', 0.0120], ['他', 0.0117], ['這', 0.0114], ['個', 0.0110],
        ['們', 0.0107], ['中', 0.0104], ['來', 0.0101], ['上', 0.0098],
        ['大', 0.0095], ['和', 0.0092], ['國', 0.0089], ['地', 0.0086],
        ['到', 0.0083], ['以', 0.0080], ['說', 0.0077], ['時', 0.0074],
        ['要', 0.0071], ['就', 0.0068], ['出', 0.0065], ['會', 0.0062],
        ['可', 0.0059], ['也', 0.0056]
      ]),
      uniqueChars: new Set(['們', '個', '為', '這', '那', '國', '華', '是', '的', '了']),
      scriptPrimary: /[\u4E00-\u9FFF\u3400-\u4DBF]/g
    });

    this._distributions.set('croatian', {
      trigrams: new Map([
        ['je ', 0.0213], ['ije', 0.0197], ['ti ', 0.0187], ['na ', 0.0176],
        ['ost', 0.0165], ['nje', 0.0154], ['ja ', 0.0143], ['ko ', 0.0132],
        ['ati', 0.0121], ['i p', 0.0110], ['mo ', 0.0105], ['li ', 0.0101],
        ['sta', 0.0097], ['no ', 0.0094], ['za ', 0.0091], ['im ', 0.0088],
        ['ma ', 0.0085], ['o p', 0.0082], ['eni', 0.0079], ['ova', 0.0076],
        ['pri', 0.0073], ['u p', 0.0070], ['aju', 0.0067], ['cij', 0.0064],
        ['a s', 0.0061], ['a p', 0.0058], ['koj', 0.0055], ['vat', 0.0052],
        ['iti', 0.0049], ['ju ', 0.0046]
      ]),
      uniqueChars: new Set(['č', 'ć', 'đ', 'š', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('czech', {
      trigrams: new Map([
        ['ní ', 0.0213], ['pro', 0.0197], ['ost', 0.0187], ['pře', 0.0176],
        ['ho ', 0.0165], ['ové', 0.0154], ['né ', 0.0143], ['ch ', 0.0132],
        ['pří', 0.0121], ['ého', 0.0110], ['na ', 0.0105], ['sta', 0.0101],
        ['ém ', 0.0097], ['st ', 0.0094], ['pod', 0.0091], ['ení', 0.0088],
        ['ých', 0.0085], ['roz', 0.0082], ['čen', 0.0079], ['tí ', 0.0076],
        ['vá ', 0.0073], ['kter', 0.0070], ['vý ', 0.0067], ['říz', 0.0064],
        ['uje', 0.0061], ['byl', 0.0058], ['při', 0.0055], ['dne', 0.0052],
        ['kte', 0.0049], ['rý ', 0.0046]
      ]),
      uniqueChars: new Set(['č', 'ď', 'ě', 'ň', 'ř', 'š', 'ť', 'ů', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('danish', {
      trigrams: new Map([
        ['er ', 0.0213], ['en ', 0.0197], ['et ', 0.0187], ['for', 0.0176],
        ['de ', 0.0165], ['der', 0.0154], ['ing', 0.0143], ['til', 0.0132],
        ['at ', 0.0121], ['og ', 0.0110], ['ikk', 0.0105], ['kke', 0.0101],
        ['har', 0.0097], ['af ', 0.0094], ['med', 0.0091], ['and', 0.0088],
        ['på ', 0.0085], ['sig', 0.0082], ['ige', 0.0079], ['end', 0.0076],
        ['ere', 0.0073], ['men', 0.0070], ['den', 0.0067], ['ang', 0.0064],
        ['hed', 0.0061], ['jeg', 0.0058], ['ska', 0.0055], ['ove', 0.0052],
        ['kun', 0.0049], ['hvi', 0.0046]
      ]),
      uniqueChars: new Set(['æ', 'ø', 'å']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    // this._distributions.set('dari', {
    //   trigrams: new Map([
    //     ['ای ', 0.0213], [' در', 0.0197], ['ان ', 0.0187], ['به ', 0.0176],
    //     ['ست ', 0.0165], ['ی ک', 0.0154], ['ند ', 0.0143], ['که ', 0.0132],
    //     ['ده ', 0.0121], ['ه م', 0.0110], ['ها ', 0.0106], ['می ', 0.0102],
    //     ['ین ', 0.0098], ['ر ا', 0.0094], ['ا د', 0.0090], ['د ک', 0.0086],
    //     ['کرد', 0.0082], ['رد ', 0.0078], ['ه ا', 0.0074], ['است', 0.0070],
    //     ['ت ک', 0.0066], ['ن م', 0.0062], ['ود ', 0.0058], ['را ', 0.0054],
    //     ['از ', 0.0050], ['ه ب', 0.0046], ['بر ', 0.0042], ['ن د', 0.0038],
    //     ['ما ', 0.0034], ['شد', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ی', 'ه', 'ک', 'گ', 'چ', 'پ', 'ژ', 'آ']),
    //   scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    // });

    this._distributions.set('dari', {
      trigrams: new Map([
        // Common Dari trigrams
        ['ای ', 0.0213], [' در', 0.0197], ['ان ', 0.0187], ['به ', 0.0176],
        ['ست ', 0.0165], ['ی ک', 0.0154], ['ند ', 0.0143], ['که ', 0.0132],
        ['ده ', 0.0121], ['ه م', 0.0110], ['ها ', 0.0106], ['می ', 0.0102],
        ['ین ', 0.0098], ['ر ا', 0.0094], ['ا د', 0.0090], ['د ک', 0.0086],
        ['کرد', 0.0082], ['رد ', 0.0078], ['ه ا', 0.0074], ['است', 0.0070],
        ['ت ک', 0.0066], ['ن م', 0.0062], ['ود ', 0.0058], ['را ', 0.0054],
        ['از ', 0.0050], ['ه ب', 0.0046], ['بر ', 0.0042], ['ن د', 0.0038],
        ['ما ', 0.0034], ['شد', 0.0030],

        // Distinctively Dari/Persian trigrams
        ['افغ', 0.0029], ['غان', 0.0028], ['انس', 0.0027], ['ستا', 0.0026],
        ['تان', 0.0025], ['می‌', 0.0024], ['‌کن', 0.0023], ['چه ', 0.0022],
        ['گی ', 0.0021], ['پر', 0.0020], ['ژه', 0.0019], ['گوی', 0.0018],
        ['خوا', 0.0017], ['واه', 0.0016], ['اهد', 0.0015], ['هد ', 0.0014],

        // Distinctively Dari (vs Arabic) trigrams
        ['گاه', 0.0013], ['پس', 0.0012], ['زی', 0.0011], ['یم', 0.0010],
        ['چرا', 0.0009], ['ی‌ش', 0.0008], ['شما', 0.0007], ['ما ', 0.0006],
        ['دیش', 0.0005], ['شب', 0.0004], ['راه', 0.0003], ['هش', 0.0002],
        ['نه‌', 0.0001]
      ]),
      uniqueChars: new Set([
        // Distinctively Persian/Dari letters not found in Arabic
        'پ', 'چ', 'ژ', 'گ', 'ی', 'ک',
        // Distinctive Persian/Dari characters
        '‌', // Zero-width non-joiner (very common in Persian/Dari)
        '٪', '؟', '٫', '،'
      ]),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    });

    this._distributions.set('dutch', {
      trigrams: new Map([
        ['de ', 0.0213], ['en ', 0.0197], ['een', 0.0187], ['het', 0.0176],
        ['ing', 0.0165], ['van', 0.0154], ['er ', 0.0143], ['ij ', 0.0132],
        ['aan', 0.0121], ['oor', 0.0110], ['te ', 0.0105], ['der', 0.0100],
        ['zij', 0.0096], ['gen', 0.0092], ['ren', 0.0088], ['in ', 0.0084],
        ['cht', 0.0080], ['ste', 0.0076], ['dat', 0.0072], ['ten', 0.0068],
        ['ijn', 0.0064], ['ver', 0.0060], ['aar', 0.0056], ['nde', 0.0052],
        ['den', 0.0048], ['men', 0.0044], ['ege', 0.0040], ['t d', 0.0036],
        ['e v', 0.0032], ['ond', 0.0028]
      ]),
      uniqueChars: new Set(['ij', 'ë', 'ï', 'ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('english', {
      trigrams: new Map([
        ['the', 0.0357], ['and', 0.0203], ['ing', 0.0178], ['ion', 0.0143],
        ['tio', 0.0139], ['ent', 0.0121], ['ati', 0.0113], ['for', 0.0112],
        ['her', 0.0108], ['ter', 0.0104], ['hat', 0.0100], ['tha', 0.0097],
        ['ere', 0.0093], ['ate', 0.0090], ['his', 0.0087], ['con', 0.0084],
        ['res', 0.0081], ['ver', 0.0078], ['all', 0.0075], ['ons', 0.0072],
        ['nce', 0.0069], ['men', 0.0066], ['ith', 0.0063], ['ted', 0.0060],
        ['ers', 0.0057], ['pro', 0.0054], ['thi', 0.0051], ['wit', 0.0048],
        ['are', 0.0045], ['ess', 0.0042]
      ]),
      uniqueChars: new Set(['q', 'w']),
      scriptPrimary: /[A-Za-z]/g
    });

    // this._distributions.set('estonian', {
    //   trigrams: new Map([
    //     ['se ', 0.0213], ['est', 0.0197], ['ja ', 0.0187], ['le ', 0.0176],
    //     ['is ', 0.0165], ['ine', 0.0154], ['aja', 0.0143], ['tud', 0.0132],
    //     ['alu', 0.0121], ['eri', 0.0110], ['oli', 0.0105], ['ma ', 0.0100],
    //     ['ise', 0.0096], ['ta ', 0.0092], ['ega', 0.0088], ['sta', 0.0084],
    //     ['da ', 0.0080], ['sel', 0.0076], ['ele', 0.0072], ['ett', 0.0068],
    //     ['esi', 0.0064], ['ida', 0.0060], ['mis', 0.0056], ['ema', 0.0052],
    //     ['ena', 0.0048], ['e v', 0.0044], ['val', 0.0040], ['iks', 0.0036],
    //     ['kas', 0.0032], ['tun', 0.0028]
    //   ]),
    //   uniqueChars: new Set(['ä', 'ö', 'õ', 'ü']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    // });

    this._distributions.set('estonian', {  trigrams: new Map([
      ['se ', 0.0213], ['est', 0.0197], ['ja ', 0.0187], ['le ', 0.0176],
        ['is ', 0.0165], ['ine', 0.0154], ['aja', 0.0143], ['tud', 0.0132],
        ['alu', 0.0121], ['eri', 0.0110], ['oli', 0.0105], ['ma ', 0.0100],
        ['ise', 0.0096], ['ta ', 0.0092], ['ega', 0.0088], ['sta', 0.0084],
        ['da ', 0.0080], ['sel', 0.0076], ['ele', 0.0072], ['ett', 0.0068],
        ['esi', 0.0064], ['ida', 0.0060], ['mis', 0.0056], ['ema', 0.0052],
        ['ena', 0.0048], ['e v', 0.0044], ['val', 0.0040], ['iks', 0.0036],
        ['kas', 0.0032], ['tun', 0.0028], ['on ', 0.0027], ['ei ', 0.0026],
        ['nud', 0.0025], ['ga ', 0.0024], ['aga', 0.0023], ['uid', 0.0022],
        ['kui', 0.0021], ['ing', 0.0020], ['nin', 0.0019], ['sid', 0.0018],
        ['id ', 0.0017], ['ks ', 0.0016], ['lik', 0.0015], ['and', 0.0014],
        ['ast', 0.0013], ['iit', 0.0012], ['õig', 0.0011], ['ärg', 0.0010],
        ['öö ', 0.0009], ['äev', 0.0008], ['õnn', 0.0007], ['päe', 0.0006],
        ['ühe', 0.0005], ['üld', 0.0004], ['õpP', 0.0003], ['öel', 0.0002],
        ['ööl', 0.0001], ['äes', 0.00009], ['sse', 0.00008], ['ata', 0.00007],
        ['des', 0.00006], ['nna', 0.00005], ['tal', 0.00004], ['lin', 0.00003],
        ['inn', 0.00002], ['nna', 0.00001], ['ees', 0.000009], ['sti', 0.000008],
        ['eest', 0.000007], ['esti', 0.000006], ['ill', 0.000005], ['lle', 0.000004],
        ['kee', 0.000003], ['eel', 0.000002], ['aeg', 0.000001]]),
      uniqueChars: new Set(['ä', 'ö', 'õ', 'ü', 'š', 'ž',    'Ä', 'Ö', 'Õ', 'Ü', 'Š', 'Ž'  ]),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('fijian', {
      trigrams: new Map([
        ['na ', 0.0213], ['ka ', 0.0197], ['a n', 0.0187], ['ni ', 0.0176],
        ['au ', 0.0165], ['vu ', 0.0154], ['aki', 0.0143], ['i n', 0.0132],
        ['va ', 0.0121], ['mai', 0.0110], ['vei', 0.0105], ['ki ', 0.0100],
        ['e n', 0.0096], ['nak', 0.0092], ['i v', 0.0088], ['kat', 0.0084],
        ['ata', 0.0080], ['ke ', 0.0076], ['vak', 0.0072], ['kau', 0.0068],
        ['tu ', 0.0064], ['aka', 0.0060], ['ai ', 0.0056], ['ko ', 0.0052],
        ['ara', 0.0048], ['ala', 0.0044], ['cak', 0.0040], ['aya', 0.0036],
        ['tou', 0.0032], ['u v', 0.0028]
      ]),
      uniqueChars: new Set(['ḡ', 'ō', 'ā', 'ē', 'ū', 'ī']), // These aren't common but sometimes used for long vowels
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('filipino', {
      trigrams: new Map([
        ['ang', 0.0213], ['ng ', 0.0197], ['an ', 0.0187], ['mga', 0.0176],
        ['sa ', 0.0165], ['at ', 0.0154], ['na ', 0.0143], ['g m', 0.0132],
        ['ay ', 0.0121], ['ila', 0.0110], ['ga ', 0.0106], ['man', 0.0102],
        ['pa ', 0.0098], ['ito', 0.0094], ['to ', 0.0090], ['ala', 0.0086],
        ['pan', 0.0082], ['aga', 0.0078], ['apa', 0.0074], ['ama', 0.0070],
        ['ka ', 0.0066], ['ako', 0.0062], ['ko ', 0.0058], ['a m', 0.0054],
        ['g p', 0.0050], ['pag', 0.0046], ['a p', 0.0042], ['gin', 0.0038],
        ['ina', 0.0034], ['yon', 0.0030]
      ]),
      uniqueChars: new Set(['ñ']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('finnish', {
      trigrams: new Map([
        ['en ', 0.0213], ['an ', 0.0197], ['ssa', 0.0187], ['in ', 0.0176],
        ['sta', 0.0165], ['nen', 0.0154], ['ett', 0.0143], ['n k', 0.0132],
        ['ise', 0.0121], ['ään', 0.0110], ['ja ', 0.0106], ['ta ', 0.0102],
        ['lla', 0.0098], ['tte', 0.0094], ['si ', 0.0090], ['ät ', 0.0086],
        ['aa ', 0.0082], ['sti', 0.0078], ['iin', 0.0074], ['tä ', 0.0070],
        ['on ', 0.0066], ['äl ', 0.0062], ['oli', 0.0058], ['ain', 0.0054],
        ['iä ', 0.0050], ['est', 0.0046], ['tää', 0.0042], ['maa', 0.0038],
        ['lai', 0.0034], ['tti', 0.0030]
      ]),
      uniqueChars: new Set(['ä', 'ö']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    // this._distributions.set('french', {
    //   trigrams: new Map([
    //     ['es ', 0.0301], ['ent', 0.0297], ['de ', 0.0261], ['le ', 0.0243],
    //     ['que', 0.0242], ['les', 0.0216], [' de', 0.0211], ['la ', 0.0194],
    //     ['on ', 0.0176], ['et ', 0.0174], ['ion', 0.0168], ['ant', 0.0163],
    //     ['ait', 0.0159], ['ans', 0.0154], ['s d', 0.0150], ['tio', 0.0146],
    //     ['e d', 0.0141], ['ne ', 0.0137], ['re ', 0.0133], ['our', 0.0129],
    //     ['des', 0.0124], ['est', 0.0120], ['e l', 0.0116], ['par', 0.0112],
    //     ['s l', 0.0108], ['lle', 0.0104], ['pas', 0.0100], ['tre', 0.0096],
    //     ['un ', 0.0092], ['en ', 0.0088]
    //   ]),
    //   uniqueChars: new Set(['ç', 'œ', 'â', 'ê', 'î', 'ô', 'û', 'ë', 'ÿ', 'é', 'è', 'ê', 'à', 'ù', 'ï']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    // });

    this._distributions.set('french', {
      trigrams: new Map([
        // Keeping core French trigrams but adjusting some frequencies
        ['es ', 0.0301], ['ent', 0.0297], ['de ', 0.0261], ['le ', 0.0243],
        ['que', 0.0222], ['les', 0.0216], [' de', 0.0211], ['la ', 0.0194],
        ['on ', 0.0176], ['et ', 0.0174], ['ion', 0.0168], ['ant', 0.0163],
        ['ait', 0.0159], ['ans', 0.0154], ['s d', 0.0150], ['tio', 0.0146],
        ['e d', 0.0141], ['ne ', 0.0137], ['re ', 0.0133], ['our', 0.0129],
        ['des', 0.0124], ['est', 0.0120], ['e l', 0.0116], ['par', 0.0112],
        ['s l', 0.0108], ['lle', 0.0104], ['pas', 0.0100], ['tre', 0.0096],
        ['un ', 0.0092], ['en ', 0.0088],

        // Highly distinctive French patterns (increasing frequency)
        ['aux', 0.0085], ['eau', 0.0083], ['œu', 0.0080], ['œ', 0.0078],
        ['oi', 0.0075], ['oin', 0.0072], ['ui', 0.0070], ['ph', 0.0068],
        ['ain', 0.0065], ['ein', 0.0062], ['onn', 0.0060], ['ien', 0.0058],
        ['out', 0.0055], ['au ', 0.0053], ['eux', 0.0050], ['oir', 0.0048],

        // Additional distinctive French patterns
        ['ç', 0.0045], ['ais', 0.0042], ['oi ', 0.0040], ['ais', 0.0038],
        ['ais ', 0.0036], ['ien', 0.0034], ['amment', 0.0032], ['emment', 0.0030],
        ['où', 0.0028], ['où ', 0.0026], ['pts', 0.0024], ['mps', 0.0022],
        ['où', 0.0020], ['êt', 0.0018], ['ît', 0.0016], ['ill', 0.0014]
      ]),
      uniqueChars: new Set([
        'ç', 'œ', 'â', 'ê', 'î', 'ô', 'û', 'ë', 'ÿ', 'é', 'è', 'ê', 'à', 'ù', 'ï',
        'Ç', 'Œ', 'Â', 'Ê', 'Î', 'Ô', 'Û', 'Ë', 'Ÿ', 'É', 'È', 'Ê', 'À', 'Ù', 'Ï'
      ]),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('french_canada', {
      trigrams: new Map([
        ['es ', 0.0280], ['ent', 0.0235], ['de ', 0.0220], ['les', 0.0200],
        ['la ', 0.0190], ['on ', 0.0175], ['le ', 0.0170], ['et ', 0.0160],
        ['que', 0.0150], ['ant', 0.0140], ['des', 0.0130], ['our', 0.0120],
        ['ion', 0.0110], ['re ', 0.0105], ['ns ', 0.0100], ['ait', 0.0095],
        ['ans', 0.0090], ['ais', 0.0085], ['est', 0.0080], ['pas', 0.0075],
        ['qui', 0.0070], ['it ', 0.0065], ['dan', 0.0060], ['ire', 0.0055],
        ['ces', 0.0050], ['par', 0.0045], ['une', 0.0040], ['res', 0.0035],
        ['eux', 0.0030], ['tou', 0.0025]
      ]),
      uniqueChars: new Set(['é', 'è', 'ê', 'à', 'ç', 'ù', 'â', 'î', 'ô', 'û', 'ë', 'ï', 'ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('galician', {
      trigrams: new Map([
        ['os ', 0.0213], ['ón ', 0.0197], ['que', 0.0187], ['ció', 0.0176],
        ['da ', 0.0165], ['as ', 0.0154], ['do ', 0.0143], ['a d', 0.0132],
        ['ión', 0.0121], ['de ', 0.0110], ['o d', 0.0105], ['e d', 0.0100],
        ['s d', 0.0096], ['n d', 0.0092], ['en ', 0.0088], ['con', 0.0084],
        ['non', 0.0080], ['ra ', 0.0076], ['ar ', 0.0072], ['la ', 0.0068],
        ['res', 0.0064], ['s p', 0.0060], ['to ', 0.0056], ['est', 0.0052],
        ['sta', 0.0048], ['n p', 0.0044], ['ao ', 0.0140], ['nte', 0.0036], // Increased weight for 'ao ' (distinctive Galician)
        ['pro', 0.0032], ['ca ', 0.0028],
        // Add distinctive Galician trigrams with high weights
        ['nha', 0.0130], ['iñ', 0.0125], ['nh', 0.0120], ['mai', 0.0115],
        ['iño', 0.0110], ['unh', 0.0105], ['ga ', 0.0100]
      ]),
      uniqueChars: new Set(['ñ', 'ó', 'á', 'é', 'í', 'ú', 'ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('georgian', {
      trigrams: new Map([
        ['ის ', 0.0213], ['და ', 0.0197], ['ში ', 0.0187], ['არ ', 0.0176],
        ['ელი', 0.0165], ['ან ', 0.0154], ['თა ', 0.0143], ['ება', 0.0132],
        ['ობა', 0.0121], ['ია ', 0.0110], ['ს ა', 0.0105], ['ა ს', 0.0100],
        ['რო ', 0.0096], ['ერ ', 0.0092], ['ს მ', 0.0088], ['მა ', 0.0084],
        ['რი ', 0.0080], ['ს ს', 0.0076], ['ივა', 0.0072], ['ვან', 0.0068],
        ['ნი ', 0.0064], ['რთ', 0.0060], ['ით ', 0.0056], ['ეს ', 0.0052],
        ['ულ', 0.0048], ['ლი ', 0.0044], ['ური', 0.0040], ['რის', 0.0036],
        ['ს გ', 0.0032], ['გა', 0.0028]
      ]),
      uniqueChars: new Set(['ა', 'ბ', 'გ', 'დ', 'ე', 'ვ', 'ზ', 'თ', 'ი', 'კ', 'ლ', 'მ', 'ნ', 'ო', 'პ', 'ჟ', 'რ', 'ს', 'ტ', 'უ', 'ფ', 'ქ', 'ღ', 'ყ', 'შ', 'ჩ', 'ც', 'ძ', 'წ', 'ჭ', 'ხ', 'ჯ', 'ჰ']),
      scriptPrimary: /[\u10A0-\u10FF]/g
    });

    this._distributions.set('german', {
      trigrams: new Map([
        ['der', 0.0375], ['ein', 0.0282], ['die', 0.0271], ['und', 0.0233],
        ['sch', 0.0224], ['ich', 0.0215], ['den', 0.0199], ['nde', 0.0186],
        ['che', 0.0178], ['gen', 0.0167], ['en ', 0.0162], ['mit', 0.0158],
        ['te ', 0.0153], ['ung', 0.0148], ['von', 0.0143], ['zu ', 0.0138],
        ['cht', 0.0133], ['ste', 0.0128], ['das', 0.0123], ['ist', 0.0118],
        ['ers', 0.0113], ['end', 0.0108], ['erk', 0.0103], ['rch', 0.0098],
        ['sen', 0.0093], ['ind', 0.0088], ['ick', 0.0083], ['it ', 0.0078],
        ['ese', 0.0073], ['st ', 0.0068]
      ]),
      uniqueChars: new Set(['ä', 'ö', 'ü', 'ß']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('greek', {
      trigrams: new Map([
        ['και', 0.0213], ['ου ', 0.0197], ['αι ', 0.0187], ['της', 0.0176],
        ['τα ', 0.0165], ['το ', 0.0154], ['ης ', 0.0143], ['απ ', 0.0132],
        ['για', 0.0121], ['με ', 0.0110], ['ον ', 0.0105], ['να ', 0.0100],
        ['μα ', 0.0096], ['ος ', 0.0092], ['των', 0.0088], ['σε ', 0.0084],
        ['ες ', 0.0080], ['οι ', 0.0076], ['εί ', 0.0072], ['από', 0.0068],
        ['ς τ', 0.0064], ['ατα', 0.0060], ['σης', 0.0056], ['αλ ', 0.0052],
        ['ι τ', 0.0048], ['ά τ', 0.0044], ['α τ', 0.0040], ['αν ', 0.0036],
        ['σμ', 0.0032], ['π τ', 0.0028]
      ]),
      uniqueChars: new Set(['α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ', 'λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ', 'φ', 'χ', 'ψ', 'ω', 'ά', 'έ', 'ή', 'ί', 'ό', 'ύ', 'ώ', 'ϊ', 'ϋ']),
      scriptPrimary: /[\u0370-\u03FF]/g
    });

    this._distributions.set('gujarati', {
      trigrams: new Map([
        ['ના ', 0.0213], ['માં', 0.0197], ['ઓ ', 0.0187], ['તા ', 0.0176],
        ['રણ', 0.0165], ['ને ', 0.0154], ['કર', 0.0143], ['ર છ', 0.0132],
        ['વા ', 0.0121], ['ે છ', 0.0110], ['ગુ', 0.0105], ['ુજ', 0.0100],
        ['જર', 0.0096], ['રા', 0.0092], ['ાત', 0.0088], ['તી', 0.0084],
        ['ીય', 0.0080], ['ય ', 0.0076], ['વિ', 0.0072], ['િક', 0.0068],
        ['કા', 0.0064], ['ાર', 0.0060], ['ર વ', 0.0056], ['વિ', 0.0052],
        ['િશ', 0.0048], ['શ્', 0.0044], ['્વ', 0.0040], ['વન', 0.0036],
        ['ના', 0.0032], ['ા સ', 0.0028]
      ]),
      uniqueChars: new Set(['અ', 'આ', 'ઇ', 'ઈ', 'ઉ', 'ઊ', 'ઋ', 'એ', 'ઐ', 'ઓ', 'ઔ', 'ક', 'ખ', 'ગ', 'ઘ', 'ઙ', 'ચ', 'છ', 'જ', 'ઝ', 'ઞ', 'ટ', 'ઠ', 'ડ', 'ઢ', 'ણ', 'ત', 'થ', 'દ', 'ધ', 'ન', 'પ', 'ફ', 'બ', 'ભ', 'મ', 'ય', 'ર', 'લ', 'વ', 'શ', 'ષ', 'સ', 'હ', 'ળ']),
      scriptPrimary: /[\u0A80-\u0AFF]/g
    });

    this._distributions.set('haitian', {
      trigrams: new Map([
        ['an ', 0.0213], ['yo ', 0.0197], ['ou ', 0.0187], ['yon', 0.0176],
        ['ki ', 0.0165], ['pou', 0.0154], ['a p', 0.0143], ['on ', 0.0132],
        ['pa ', 0.0121], ['li ', 0.0110], ['n y', 0.0106], ['yo', 0.0102],
        ['èk ', 0.0098], ['la ', 0.0094], ['men', 0.0090], ['nan', 0.0086],
        ['te ', 0.0082], ['en ', 0.0078], ['ale', 0.0074], ['le ', 0.0070],
        ['è a', 0.0066], ['ak ', 0.0062], ['kon', 0.0058], ['gen', 0.0054],
        ['se ', 0.0050], ['ap ', 0.0046], ['p k', 0.0042], ['k l', 0.0038],
        ['de ', 0.0034], ['oun', 0.0030]
      ]),
      uniqueChars: new Set(['è', 'ò', 'à']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('hausa', {
      trigrams: new Map([
        ['in ', 0.0213], ['an ', 0.0197], ['ya ', 0.0187], ['a d', 0.0176],
        ['wa ', 0.0165], ['da ', 0.0154], ['n d', 0.0143], ['ta ', 0.0132],
        ['a k', 0.0121], ['kin', 0.0110], ['ar ', 0.0105], ['a s', 0.0100],
        ['a a', 0.0096], ['ni ', 0.0092], ['na ', 0.0088], ['iya', 0.0084],
        ['shi', 0.0080], ['i d', 0.0076], ['kan', 0.0072], ['yan', 0.0068],
        ['a m', 0.0064], ['ma ', 0.0060], ['a y', 0.0056], ['a t', 0.0052],
        ['a w', 0.0048], ['a b', 0.0044], ['ne ', 0.0040], ['a h', 0.0036],
        ['kar', 0.0032], ['asa', 0.0028]
      ]),
      uniqueChars: new Set(['ɓ', 'ɗ', 'ƙ', 'ƴ']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0180-\u024F]/g
    });

    this._distributions.set('hebrew', {
      trigrams: new Map([
        ['של', 0.0255], ['את', 0.0206], ['לא', 0.0183], ['הוא', 0.0172],
        ['היא', 0.0164], ['עם', 0.0158], ['על', 0.0154], ['אני', 0.0142],
        ['הם', 0.0137], ['לי', 0.0126], ['כל', 0.0121], ['זה', 0.0116],
        ['לה', 0.0111], ['אבל', 0.0106], ['ים', 0.0102], ['ות', 0.0098],
        ['רק', 0.0094], ['מה', 0.0090], ['אם', 0.0086], ['גם', 0.0082],
        ['הי', 0.0078], ['הש', 0.0074], ['של', 0.0070], ['לו', 0.0066],
        ['יה', 0.0062], ['בר', 0.0058], ['ש ה', 0.0054], ['צה', 0.0050],
        ['ומ', 0.0046], ['ול', 0.0042]
      ]),
      uniqueChars: new Set(['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י', 'כ', 'ך', 'ל', 'מ', 'ם', 'נ', 'ן', 'ס', 'ע', 'פ', 'ף', 'צ', 'ץ', 'ק', 'ר', 'ש', 'ת']),
      scriptPrimary: /[\u0590-\u05FF\uFB1D-\uFB4F]/g
    });

    this._distributions.set('hindi', {
      trigrams: new Map([
        ['का ', 0.0198], ['के ', 0.0187], ['में', 0.0172], ['है ', 0.0155],
        ['की ', 0.0152], ['और', 0.0147], ['से ', 0.0135], ['को ', 0.0129],
        ['एक ', 0.0122], ['पर ', 0.0118], ['यह ', 0.0114], ['कर ', 0.0110],
        ['रा ', 0.0107], ['ने ', 0.0104], ['ता ', 0.0101], ['या ', 0.0098],
        ['ार ', 0.0095], ['नह', 0.0092], ['हीं', 0.0089], ['कि ', 0.0086],
        ['ना ', 0.0083], ['हो ', 0.0080], ['जा ', 0.0077], ['था ', 0.0074],
        ['ों ', 0.0071], ['ति ', 0.0068], ['ित ', 0.0065], ['ले ', 0.0062],
        ['सक', 0.0059], ['ये ', 0.0056]
      ]),
      uniqueChars: new Set(['क', 'ख', 'ग', 'घ', 'ङ', 'च', 'छ', 'ज', 'झ', 'ञ', 'ट', 'ठ', 'ड', 'ढ', 'ण', 'त', 'थ', 'द', 'ध', 'न', 'प', 'फ', 'ब', 'भ', 'म', 'य', 'र', 'ल', 'व', 'श', 'ष', 'स', 'ह', 'ऋ', 'ॠ', 'ऌ', 'ॡ']),
      scriptPrimary: /[\u0900-\u097F]/g
    });

    this._distributions.set('hmong', {
      trigrams: new Map([
        ['ia ', 0.0213], ['ab ', 0.0197], ['ua ', 0.0187], ['uas', 0.0176],
        ['lub', 0.0165], ['ib ', 0.0154], ['tsi', 0.0143], ['hau', 0.0132],
        ['tog', 0.0121], ['nts', 0.0110], ['oj ', 0.0105], ['g t', 0.0100],
        ['tsh', 0.0096], ['sha', 0.0092], ['hab', 0.0088], ['b t', 0.0084],
        ['tsa', 0.0080], ['awn', 0.0076], ['wn ', 0.0072], ['cia', 0.0068],
        ['raw', 0.0064], ['m t', 0.0060], ['an ', 0.0056], ['am ', 0.0052],
        ['tam', 0.0048], ['siv', 0.0044], ['iv ', 0.0040], ['tu ', 0.0036],
        ['lis', 0.0032], ['is ', 0.0028]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('hungarian', {
      trigrams: new Map([
        ['és ', 0.0213], ['en ', 0.0197], ['ek ', 0.0187], ['ett', 0.0176],
        ['sze', 0.0165], ['meg', 0.0154], ['nak', 0.0143], ['min', 0.0132],
        ['ter', 0.0121], ['an ', 0.0110], ['ogy', 0.0105], ['gya', 0.0101],
        ['val', 0.0097], ['int', 0.0094], ['tet', 0.0091], ['et ', 0.0088],
        ['ás ', 0.0085], ['is ', 0.0082], ['ere', 0.0079], ['kor', 0.0076],
        ['lem', 0.0073], ['em ', 0.0070], ['hoz', 0.0067], ['zet', 0.0064],
        ['ele', 0.0061], ['tek', 0.0058], ['ott', 0.0055], ['ara', 0.0052],
        ['ha ', 0.0049], ['zt ', 0.0046]
      ]),
      uniqueChars: new Set(['á', 'é', 'í', 'ó', 'ö', 'ő', 'ú', 'ü', 'ű']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('icelandic', {
      trigrams: new Map([
        ['að ', 0.0213], ['um ', 0.0197], ['ir ', 0.0187], ['og ', 0.0176],
        ['nn ', 0.0165], ['ar ', 0.0154], ['ið ', 0.0143], ['sem', 0.0132],
        ['na ', 0.0121], ['ur ', 0.0110], ['ega', 0.0106], ['inn', 0.0102],
        ['ga ', 0.0098], ['und', 0.0094], ['di ', 0.0090], ['fi ', 0.0086],
        ['ins', 0.0082], ['ki ', 0.0078], ['st ', 0.0074], ['ð s', 0.0070],
        ['eð ', 0.0066], ['n s', 0.0062], ['leg', 0.0058], ['r s', 0.0054],
        ['ði ', 0.0050], ['ek ', 0.0046], ['g s', 0.0042], ['inu', 0.0038],
        ['fra', 0.0034], ['ama', 0.0030]
      ]),
      uniqueChars: new Set(['á', 'é', 'í', 'ó', 'ú', 'ý', 'þ', 'æ', 'ð', 'ö']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u00DE\u00FE]/g
    });

    this._distributions.set('igbo', {
      trigrams: new Map([
        ['na ', 0.0213], ['a n', 0.0197], ['nke', 0.0187], ['ere', 0.0176],
        ['nye', 0.0165], ['di ', 0.0154], ['e n', 0.0143], ['ka ', 0.0132],
        ['kwa', 0.0121], ['ọ n', 0.0110], ['n a', 0.0106], ['ma ', 0.0102],
        ['ike', 0.0098], ['aka', 0.0094], ['ya ', 0.0090], ['iri', 0.0086],
        ['ihe', 0.0082], ['ị n', 0.0078], ['ri ', 0.0074], ['dị ', 0.0070],
        ['ụ n', 0.0066], ['i n', 0.0062], ['nwe', 0.0058], ['chi', 0.0054],
        ['a a', 0.0050], ['ụla', 0.0046], ['la ', 0.0042], ['bu ', 0.0038],
        ['ndi', 0.0034], ['ime', 0.0030]
      ]),
      uniqueChars: new Set(['ụ', 'ọ', 'ị', 'ṅ', 'ǹ']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('indonesian', {
      trigrams: new Map([
        ['an ', 0.0213], ['eng', 0.0197], ['kan', 0.0187], ['ng ', 0.0176],
        ['yan', 0.0165], ['men', 0.0154], ['ber', 0.0143], ['di ', 0.0132],
        ['ter', 0.0121], ['dan', 0.0110], ['nya', 0.0107], ['ran', 0.0104],
        ['mem', 0.0101], ['tid', 0.0098], ['dak', 0.0095], ['ung', 0.0092],
        ['per', 0.0089], ['jad', 0.0086], ['adi', 0.0083], ['itu', 0.0080],
        ['pen', 0.0077], ['nga', 0.0074], ['ara', 0.0071], ['aya', 0.0068],
        ['ami', 0.0065], ['aka', 0.0062], ['ini', 0.0059], ['pad', 0.0056],
        ['bat', 0.0053], ['ata', 0.0050]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('irish', {
      trigrams: new Map([
        ['ach', 0.0213], ['na ', 0.0197], ['ar ', 0.0187], ['an ', 0.0176],
        ['ch ', 0.0165], ['ea ', 0.0154], ['air', 0.0143], ['in ', 0.0132],
        ['is ', 0.0121], ['adh', 0.0110], ['ai ', 0.0106], ['id ', 0.0102],
        ['tha', 0.0098], ['ha ', 0.0094], ['agus', 0.0090], ['us ', 0.0086],
        ['ar ', 0.0082], ['a b', 0.0078], ['bhi', 0.0074], ['hi ', 0.0070],
        ['i g', 0.0066], ['go ', 0.0062], ['ait', 0.0058], ['ta ', 0.0054],
        ['a c', 0.0050], ['chuir', 0.0046], ['uir', 0.0042], ['ir ', 0.0038],
        ['lea', 0.0034], ['ead', 0.0030]
      ]),
      uniqueChars: new Set(['á', 'é', 'í', 'ó', 'ú']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('isixhosa', {
      trigrams: new Map([
        ['nga', 0.0213], ['le ', 0.0197], ['uku', 0.0187], ['ba ', 0.0176],
        ['pha', 0.0165], ['zo ', 0.0154], ['enz', 0.0143], ['esi', 0.0132],
        ['elo', 0.0121], ['a k', 0.0110], ['i k', 0.0105], ['kwa', 0.0100],
        ['wa ', 0.0096], ['a n', 0.0092], ['nye', 0.0088], ['ama', 0.0084],
        ['ma ', 0.0080], ['nzi', 0.0076], ['zi ', 0.0072], ['eth', 0.0068],
        ['tha', 0.0064], ['ula', 0.0060], ['lan', 0.0056], ['ala', 0.0052],
        ['a i', 0.0048], ['ise', 0.0044], ['set', 0.0040], ['etu', 0.0036],
        ['tu ', 0.0032], ['ben', 0.0028]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    // this._distributions.set('italian', {
    //   trigrams: new Map([
    //     ['che', 0.0298], ['di ', 0.0271], ['la ', 0.0245], ['del', 0.0232],
    //     ['per', 0.0219], ['to ', 0.0205], ['con', 0.0193], ['le ', 0.0182],
    //     ['ent', 0.0174], ['are', 0.0167], ['ti ', 0.0159], ['re ', 0.0152],
    //     ['ato', 0.0146], ['e d', 0.0139], ['i d', 0.0133], ['gli', 0.0128],
    //     ['ta ', 0.0123], ['ne ', 0.0118], ['pro', 0.0113], ['o d', 0.0108],
    //     ['tto', 0.0104], ['ell', 0.0099], ['ion', 0.0095], ['te ', 0.0091],
    //     ['no ', 0.0087], ['in ', 0.0083], ['al ', 0.0080], ['ano', 0.0077],
    //     ['lla', 0.0074], ['nte', 0.0071]
    //   ]),
    //   uniqueChars: new Set(['à', 'è', 'é', 'ì', 'í', 'ò', 'ó', 'ù', 'ú']),
    //   scriptPrimary: /[A-Za-zÀàÈèÉéÌìÍíÒòÓóÙùÚú]/g
    // });

    this._distributions.set('italian', {
      trigrams: new Map([
        // Current high-frequency trigrams
        ['che', 0.0298], ['di ', 0.0271], ['la ', 0.0245], ['del', 0.0232],
        ['per', 0.0219], ['to ', 0.0205], ['con', 0.0193], ['le ', 0.0182],
        ['ent', 0.0174], ['are', 0.0167], ['ti ', 0.0159], ['re ', 0.0152],
        ['ato', 0.0146], ['e d', 0.0139], ['i d', 0.0133], ['gli', 0.0128],
        ['ta ', 0.0123], ['ne ', 0.0118], ['pro', 0.0113], ['o d', 0.0108],
        ['tto', 0.0104], ['ell', 0.0099], ['ion', 0.0095], ['te ', 0.0091],
        ['no ', 0.0087], ['in ', 0.0083], ['al ', 0.0080], ['ano', 0.0077],
        ['lla', 0.0074], ['nte', 0.0071],

        // Additional common Italian trigrams
        ['un ', 0.0068], ['è ', 0.0065], ['na ', 0.0062], ['zia', 0.0059],
        ['non', 0.0056], ['a p', 0.0053], ['i s', 0.0050], ['o p', 0.0047],
        ['o a', 0.0044], ['e l', 0.0041], ['me ', 0.0038], ['e p', 0.0035],
        ['oni', 0.0032], ['tti', 0.0029], ['gio', 0.0026], ['ita', 0.0023],

        // Distinctive Italian trigrams
        ['sci', 0.0020], ['sch', 0.0017], ['gn', 0.0014], ['gna', 0.0011],
        ['gno', 0.0008], ['cch', 0.0005], ['zz', 0.0004], ['zza', 0.0003],
        ['cci', 0.0002], ['gli', 0.0001], ['glie', 0.00009], ['ghi', 0.00008],
        ['gli', 0.00007], ['z', 0.00006], ['z ', 0.00005]
      ]),
      uniqueChars: new Set(['à', 'è', 'é', 'ì', 'í', 'ò', 'ó', 'ù', 'ú', 'À', 'È', 'É', 'Ì', 'Í', 'Ò', 'Ó', 'Ù', 'Ú']),
      scriptPrimary: /[A-Za-zÀàÈèÉéÌìÍíÒòÓóÙùÚú]/g
    });

    this._distributions.set('japanese', {
      trigrams: new Map([
        ['する', 0.0165], ['いる', 0.0141], ['れる', 0.0128], ['なる', 0.0118],
        ['こと', 0.0112], ['いう', 0.0103], ['ない', 0.0098], ['です', 0.0091],
        ['あり', 0.0089], ['から', 0.0085], ['れて', 0.0082], ['ては', 0.0079],
        ['ます', 0.0076], ['した', 0.0073], ['ので', 0.0069], ['でき', 0.0066],
        ['もの', 0.0063], ['それ', 0.0061], ['この', 0.0058], ['ある', 0.0055],
        ['くる', 0.0052], ['とい', 0.0049], ['いっ', 0.0046], ['って', 0.0043],
        ['しま', 0.0041], ['まし', 0.0038], ['した', 0.0035], ['たが', 0.0032],
        ['がで', 0.0029], ['でき', 0.0026]
      ]),
      uniqueChars: new Set(['あ', 'い', 'う', 'え', 'お', 'か', 'き', 'く', 'け', 'こ', 'さ', 'し', 'す', 'せ', 'そ', 'た', 'ち', 'つ', 'て', 'と', 'な', 'に', 'ぬ', 'ね', 'の', 'は', 'ひ', 'ふ', 'へ', 'ほ', 'ま', 'み', 'む', 'め', 'も', 'や', 'ゆ', 'よ', 'ら', 'り', 'る', 'れ', 'ろ', 'わ', 'を', 'ん', 'が', 'ぎ', 'ぐ', 'げ', 'ご', 'ざ', 'じ', 'ず', 'ぜ', 'ぞ', 'だ', 'ぢ', 'づ', 'で', 'ど', 'ば', 'び', 'ぶ', 'べ', 'ぼ', 'ぱ', 'ぴ', 'ぷ', 'ぺ', 'ぽ']),
      scriptPrimary: /[\u3040-\u30FF]/g,
      scriptSecondary: /[\u4E00-\u9FFF\u3400-\u4DBF]/g
    });

    this._distributions.set('kannada', {
      trigrams: new Map([
        ['ದೆ ', 0.0213], ['ಗಳ', 0.0197], ['ರು ', 0.0187], ['ದ ', 0.0176],
        ['ಗೆ ', 0.0165], ['ಾಗಿ', 0.0154], ['ಲ್ಲ', 0.0143], ['ನ್ನ', 0.0132],
        ['ಾದ', 0.0121], ['ಿನ', 0.0110], ['ನಾ', 0.0106], ['ಾನ', 0.0102],
        ['ವಾ', 0.0098], ['ಾವ', 0.0094], ['ದಾ', 0.0090], ['ದರ', 0.0086],
        ['ರೆ', 0.0082], ['ಲಿ', 0.0078], ['ೆಲ', 0.0074], ['ಲು', 0.0070],
        ['ದ್ದ', 0.0066], ['ಿದ', 0.0062], ['ದೇ', 0.0058], ['ೇಶ', 0.0054],
        ['ಶದ', 0.0050], ['ದಲ', 0.0046], ['ಿರು', 0.0042], ['ರುವ', 0.0038],
        ['ವ ', 0.0034], ['ಕನ', 0.0030]
      ]),
      uniqueChars: new Set(['ಅ', 'ಆ', 'ಇ', 'ಈ', 'ಉ', 'ಊ', 'ಋ', 'ೠ', 'ಎ', 'ಏ', 'ಐ', 'ಒ', 'ಓ', 'ಔ', 'ಂ', 'ಃ', 'ಕ', 'ಖ', 'ಗ', 'ಘ', 'ಙ', 'ಚ', 'ಛ', 'ಜ', 'ಝ', 'ಞ', 'ಟ', 'ಠ', 'ಡ', 'ಢ', 'ಣ', 'ತ', 'ಥ', 'ದ', 'ಧ', 'ನ', 'ಪ', 'ಫ', 'ಬ', 'ಭ', 'ಮ', 'ಯ', 'ರ', 'ಱ', 'ಲ', 'ಳ', 'ವ', 'ಶ', 'ಷ', 'ಸ', 'ಹ']),
      scriptPrimary: /[\u0C80-\u0CFF]/g
    });

    // this._distributions.set('kazakh', {
    //   trigrams: new Map([
    //     ['ың ', 0.0213], ['тар', 0.0197], ['ен ', 0.0187], ['ған', 0.0176],
    //     ['лар', 0.0165], ['да ', 0.0154], ['ты ', 0.0143], ['ан ', 0.0132],
    //     ['дың', 0.0121], ['ер ', 0.0110], ['ген', 0.0105], ['нда', 0.0101],
    //     ['ала', 0.0097], ['ірі', 0.0094], ['бол', 0.0091], ['ол ', 0.0088],
    //     ['дар', 0.0085], ['қаз', 0.0082], ['аза', 0.0079], ['зақ', 0.0076],
    //     ['ақс', 0.0073], ['қст', 0.0070], ['ста', 0.0067], ['тан', 0.0064],
    //     ['ан ', 0.0061], ['н р', 0.0058], ['рес', 0.0055], ['есп', 0.0052],
    //     ['спу', 0.0049], ['пуб', 0.0046]
    //   ]),
    //   uniqueChars: new Set(['қ', 'ғ', 'ң', 'ү', 'ұ', 'ә', 'і', 'ө']),
    //   scriptPrimary: /[\u0400-\u04FF]/g
    // });

        this._distributions.set('kazakh', {
      trigrams: new Map([
        // Common Kazakh trigrams
        ['ың ', 0.0213], ['тар', 0.0197], ['ен ', 0.0187], ['ған', 0.0176],
        ['лар', 0.0165], ['да ', 0.0154], ['ты ', 0.0143], ['ан ', 0.0132],
        ['дың', 0.0121], ['ер ', 0.0110], ['ген', 0.0105], ['нда', 0.0101],
        ['ала', 0.0097], ['ірі', 0.0094], ['бол', 0.0091], ['ол ', 0.0088],
        ['дар', 0.0085], ['қаз', 0.0082], ['аза', 0.0079], ['зақ', 0.0076],
        ['ақс', 0.0073], ['қст', 0.0070], ['ста', 0.0067], ['тан', 0.0064],
        ['ан ', 0.0061], ['н р', 0.0058], ['рес', 0.0055], ['есп', 0.0052],
        ['спу', 0.0049], ['пуб', 0.0046],

        // Distinctive Kazakh trigrams
        ['қаз', 0.0044], ['аза', 0.0042], ['зақ', 0.0040], ['ақс', 0.0038],
        ['қст', 0.0036], ['ста', 0.0034], ['тан', 0.0032], ['ан ', 0.0030],
        ['нұр', 0.0028], ['ұр-', 0.0026], ['р-с', 0.0024], ['сұл', 0.0022],
        ['ұлт', 0.0020], ['лта', 0.0018], ['тан', 0.0016], ['н а', 0.0014],

        // Kazakh-specific patterns with distinctive letters
        ['құр', 0.0012], ['ұрм', 0.0010], ['рме', 0.0008], ['ет ', 0.0006],
        ['т б', 0.0005], ['бар', 0.0004], ['арл', 0.0003], ['лық', 0.0002],
        ['ық ', 0.0001], ['ң к', 0.00009], ['кел', 0.00008], ['елі', 0.00007]
      ]),
      uniqueChars: new Set([
        // Distinctive Kazakh Cyrillic characters
        'қ', 'ғ', 'ң', 'ү', 'ұ', 'ә', 'і', 'ө', 'Қ', 'Ғ', 'Ң', 'Ү', 'Ұ', 'Ә', 'І', 'Ө',
        // Other Cyrillic letters that help differentiate
        'ы', 'ь', 'ъ'
      ]),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('khmer', {
      trigrams: new Map([
        ['នៅ', 0.0213], ['ក្ន', 0.0197], ['ការ', 0.0187], ['រប', 0.0176],
        ['ង​', 0.0165], ['ជា', 0.0154], ['មួយ', 0.0143], ['ខ្ម', 0.0132],
        ['គឺ', 0.0121], ['ស់', 0.0110], ['ប្', 0.0106], ['្រ', 0.0102],
        ['រទ', 0.0098], ['ទេ', 0.0094], ['េស', 0.0090], ['សក', 0.0086],
        ['កម', 0.0082], ['ម្', 0.0078], ['្ព', 0.0074], ['ពុ', 0.0070],
        ['ុជ', 0.0066], ['ជា', 0.0062], ['ាន', 0.0058], ['នូ', 0.0054],
        ['ូវ', 0.0050], ['វក', 0.0046], ['កា', 0.0042], ['ារ', 0.0038],
        ['រដ', 0.0034], ['ដ្', 0.0030]
      ]),
      uniqueChars: new Set(['ក', 'ខ', 'គ', 'ឃ', 'ង', 'ច', 'ឆ', 'ជ', 'ឈ', 'ញ', 'ដ', 'ឋ', 'ឌ', 'ឍ', 'ណ', 'ត', 'ថ', 'ទ', 'ធ', 'ន', 'ប', 'ផ', 'ព', 'ភ', 'ម', 'យ', 'រ', 'ល', 'វ', 'ឝ', 'ឞ', 'ស', 'ហ', 'ឡ', 'អ']),
      scriptPrimary: /[\u1780-\u17FF]/g
    });

    this._distributions.set('kinyarwanda', {
      trigrams: new Map([
        ['mu ', 0.0213], ['wa ', 0.0197], ['na ', 0.0187], ['ku ', 0.0176],
        ['ndi', 0.0165], ['ira', 0.0154], ['ba ', 0.0143], ['ye ', 0.0132],
        ['ko ', 0.0121], ['aba', 0.0110], ['ra ', 0.0106], ['za ', 0.0102],
        ['ama', 0.0098], ['ntu', 0.0094], ['tu ', 0.0090], ['aga', 0.0086],
        ['ga ', 0.0082], ['zo ', 0.0078], ['u k', 0.0074], ['yo ', 0.0070],
        ['a k', 0.0066], ['byo', 0.0062], ['ni ', 0.0058], ['a n', 0.0054],
        ['gan', 0.0050], ['and', 0.0046], ['nda', 0.0042], ['da ', 0.0038],
        ['anz', 0.0034], ['nze', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('korean', {
      trigrams: new Map([
        ['이', 0.0276], ['는', 0.0193], ['다', 0.0186], ['에', 0.0173],
        ['하', 0.0156], ['고', 0.0145], ['을', 0.0139], ['가', 0.0121],
        ['지', 0.0119], ['서', 0.0113], ['한', 0.0109], ['국', 0.0103],
        ['에서', 0.0097], ['인', 0.0094], ['으로', 0.0091], ['로', 0.0088],
        ['으', 0.0084], ['며', 0.0080], ['의', 0.0076], ['것', 0.0072],
        ['여', 0.0069], ['있', 0.0065], ['대', 0.0061], ['그', 0.0058],
        ['들', 0.0054], ['도', 0.0050], ['요', 0.0047], ['게', 0.0044],
        ['전', 0.0041], ['장', 0.0038]
      ]),
      uniqueChars: new Set(['한', '국', '어', '의', '말']),
      scriptPrimary: /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/g
    });

    // this._distributions.set('kyrgyz', {
    //   trigrams: new Map([
    //     ['да ', 0.0213], ['ын ', 0.0197], ['ар ', 0.0187], ['лар', 0.0176],
    //     ['дын', 0.0165], ['ген', 0.0154], ['ен ', 0.0143], ['га ', 0.0132],
    //     ['тар', 0.0121], ['анд', 0.0110], ['кыр', 0.0105], ['ырг', 0.0101],
    //     ['ргы', 0.0097], ['гыз', 0.0094], ['зст', 0.0091], ['ста', 0.0088],
    //     ['тан', 0.0085], ['ан ', 0.0082], ['нда', 0.0079], ['дар', 0.0076],
    //     ['рда', 0.0073], ['бол', 0.0070], ['ол ', 0.0067], ['лго', 0.0064],
    //     ['гон', 0.0061], ['он ', 0.0058], ['н к', 0.0055], ['кен', 0.0052],
    //     ['енд', 0.0049], ['нде', 0.0046]
    //   ]),
    //   uniqueChars: new Set(['ң', 'ү', 'ө', 'ж', 'з']),
    //   scriptPrimary: /[\u0400-\u04FF]/g
    // });

    this._distributions.set('kyrgyz', {
      trigrams: new Map([
        // Common Kyrgyz trigrams
        ['да ', 0.0213], ['ын ', 0.0197], ['ар ', 0.0187], ['лар', 0.0176],
        ['дын', 0.0165], ['ген', 0.0154], ['ен ', 0.0143], ['га ', 0.0132],
        ['тар', 0.0121], ['анд', 0.0110], ['кыр', 0.0105], ['ырг', 0.0101],
        ['ргы', 0.0097], ['гыз', 0.0094], ['зст', 0.0091], ['ста', 0.0088],
        ['тан', 0.0085], ['ан ', 0.0082], ['нда', 0.0079], ['дар', 0.0076],
        ['рда', 0.0073], ['бол', 0.0070], ['ол ', 0.0067], ['лго', 0.0064],
        ['гон', 0.0061], ['он ', 0.0058], ['н к', 0.0055], ['кен', 0.0052],
        ['енд', 0.0049], ['нде', 0.0046],

        // Distinctive Kyrgyz trigrams
        ['кыр', 0.0044], ['ырг', 0.0042], ['ргы', 0.0040], ['гыз', 0.0038],
        ['зст', 0.0036], ['ста', 0.0034], ['тан', 0.0032], ['анд', 0.0030],
        ['нда', 0.0028], ['да ', 0.0026], ['а б', 0.0024], ['биш', 0.0022],
        ['ишк', 0.0020], ['шке', 0.0018], ['кек', 0.0016], ['ек ', 0.0014],

        // Kyrgyz-specific patterns with distinctive letters
        ['ң к', 0.0012], ['көл', 0.0010], ['өлү', 0.0008], ['лүн', 0.0006],
        ['үн ', 0.0005], ['н ж', 0.0004], ['жол', 0.0003], ['олд', 0.0002],
        ['лдо', 0.0001], ['дор', 0.00009], ['орт', 0.00008], ['тоо', 0.00007]
      ]),
      uniqueChars: new Set([
        // Distinctive Kyrgyz Cyrillic characters
        'ң', 'ү', 'ө', 'ж', 'з', 'Ң', 'Ү', 'Ө', 'Ж', 'З',
        // Other Cyrillic letters that help differentiate from non-Cyrillic Turkic languages
        'ы', 'ь', 'ъ'
      ]),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('lao', {
      trigrams: new Map([
        ['ານ', 0.0213], ['ການ', 0.0197], ['າວ', 0.0187], ['ລາ', 0.0176],
        ['ອງ', 0.0165], ['ເປ', 0.0154], ['ປັນ', 0.0143], ['ນ ', 0.0132],
        ['ະ ', 0.0121], ['ບ ', 0.0110], ['ມີ', 0.0106], ['ີກ', 0.0102],
        ['ກາ', 0.0098], ['ານ', 0.0094], ['ນລ', 0.0090], ['ລັ', 0.0086],
        ['ັດ', 0.0082], ['ດຖ', 0.0078], ['ຖະ', 0.0074], ['ະບ', 0.0070],
        ['ບາ', 0.0066], ['ານ', 0.0062], ['ນຂ', 0.0058], ['ຂອ', 0.0054],
        ['ອງ', 0.0050], ['ງລ', 0.0046], ['ລາ', 0.0042], ['າວ', 0.0038],
        ['ວ ', 0.0034], ['ຈະ', 0.0030]
      ]),
      uniqueChars: new Set(['ກ', 'ຂ', 'ຄ', 'ງ', 'ຈ', 'ສ', 'ຊ', 'ຍ', 'ດ', 'ຕ', 'ຖ', 'ທ', 'ນ', 'ບ', 'ປ', 'ຜ', 'ຝ', 'ພ', 'ຟ', 'ມ', 'ຢ', 'ຣ', 'ລ', 'ວ', 'ຫ', 'ອ', 'ຮ']),
      scriptPrimary: /[\u0E80-\u0EFF]/g
    });

    this._distributions.set('latvian', {
      trigrams: new Map([
        ['as ', 0.0213], ['ies', 0.0197], ['un ', 0.0187], ['ai ', 0.0176],
        ['ja ', 0.0165], ['is ', 0.0154], ['es ', 0.0143], ['tu ', 0.0132],
        ['um ', 0.0121], ['kas', 0.0110], ['s p', 0.0106], ['par', 0.0102],
        ['ar ', 0.0098], ['s a', 0.0094], ['tā ', 0.0090], ['us ', 0.0086],
        ['iem', 0.0082], ['em ', 0.0078], ['ā ', 0.0074], ['ām ', 0.0070],
        ['s n', 0.0066], ['no ', 0.0062], ['s i', 0.0058], ['iet', 0.0054],
        ['ka ', 0.0050], ['jau', 0.0046], ['s l', 0.0042], ['lai', 0.0038],
        ['iet', 0.0034], ['ik ', 0.0030]
      ]),
      uniqueChars: new Set(['ā', 'č', 'ē', 'ģ', 'ī', 'ķ', 'ļ', 'ņ', 'š', 'ū', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    // this._distributions.set('lithuanian', {
    //   trigrams: new Map([
    //     ['os ', 0.0213], ['ir ', 0.0197], ['ti ', 0.0187], ['ai ', 0.0176],
    //     ['as ', 0.0165], ['is ', 0.0154], ['aus', 0.0143], ['ių ', 0.0132],
    //     ['ini', 0.0121], ['iai', 0.0110], ['ius', 0.0105], ['s p', 0.0100],
    //     ['kad', 0.0096], ['ad ', 0.0092], ['tai', 0.0088], ['iš ', 0.0084],
    //     ['vie', 0.0080], ['etų', 0.0076], ['tų ', 0.0072], ['kur', 0.0068],
    //     ['ų p', 0.0064], ['io ', 0.0060], ['s t', 0.0056], ['bet', 0.0052],
    //     ['et ', 0.0048], ['nes', 0.0044], ['nis', 0.0040], ['į ', 0.0036],
    //     ['tur', 0.0032], ['uri', 0.0028]
    //   ]),
    //   uniqueChars: new Set(['ą', 'č', 'ę', 'ė', 'į', 'š', 'ų', 'ū', 'ž']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // });
    this._distributions.set('lithuanian', {
      trigrams: new Map([
        // Current high-frequency trigrams
        ['os ', 0.0213], ['ir ', 0.0197], ['ti ', 0.0187], ['ai ', 0.0176],
        ['as ', 0.0165], ['is ', 0.0154], ['aus', 0.0143], ['ių ', 0.0132],
        ['ini', 0.0121], ['iai', 0.0110], ['ius', 0.0105], ['s p', 0.0100],
        ['kad', 0.0096], ['ad ', 0.0092], ['tai', 0.0088], ['iš ', 0.0084],
        ['vie', 0.0080], ['etų', 0.0076], ['tų ', 0.0072], ['kur', 0.0068],
        ['ų p', 0.0064], ['io ', 0.0060], ['s t', 0.0056], ['bet', 0.0052],
        ['et ', 0.0048], ['nes', 0.0044], ['nis', 0.0040], ['į ', 0.0036],
        ['tur', 0.0032], ['uri', 0.0028],

        // Additional common Lithuanian trigrams
        ['yra', 0.0027], ['avo', 0.0026], ['ius', 0.0025], ['per', 0.0024],
        ['ėti', 0.0023], ['ina', 0.0022], ['ant', 0.0021], ['ies', 0.0020],
        ['kas', 0.0019], ['šia', 0.0018], ['ų s', 0.0017], ['i n', 0.0016],
        ['ian', 0.0015], ['ime', 0.0014], ['tis', 0.0013], ['lia', 0.0012],

        // Distinctive Lithuanian trigrams with diacritical characters
        ['ėje', 0.0011], ['ėjo', 0.0010], ['ąs ', 0.0009], ['ęs ', 0.0008],
        ['žia', 0.0007], ['šta', 0.0006], ['čia', 0.0005], ['įsi', 0.0004],
        ['ūs ', 0.0003], ['ąjį', 0.0002], ['ųjų', 0.0001], ['ėjų', 0.00009],

        // Common Lithuanian word stems and grammatical endings
        ['ams', 0.00008], ['oms', 0.00007], ['imi', 0.00006], ['ais', 0.00005],
        ['yje', 0.00004], ['ėse', 0.00003], ['ose', 0.00002], ['ame', 0.00001],

        // Additional distinctive trigrams derived from common Lithuanian words
        ['kal', 0.000009], ['lie', 0.000008], ['tuv', 0.000007], ['uvi', 0.000006],
        ['pra', 0.000005], ['rei', 0.000004], ['men', 0.000003], ['eni', 0.000002],
        ['esi', 0.000001]
      ]),
      uniqueChars: new Set([
        // All Lithuanian special characters in both lowercase and uppercase
        'ą', 'č', 'ę', 'ė', 'į', 'š', 'ų', 'ū', 'ž',
        'Ą', 'Č', 'Ę', 'Ė', 'Į', 'Š', 'Ų', 'Ū', 'Ž',
        // Including the distinctive collation of y between į and j
        'y', 'Y'
      ]),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('luxembourgish', {
      trigrams: new Map([
        ['en ', 0.0213], ['er ', 0.0197], ['sch', 0.0187], ['ch ', 0.0176],
        ['den', 0.0165], ['an ', 0.0154], ['een', 0.0143], ['de ', 0.0132],
        ['net', 0.0121], ['och', 0.0110], ['n d', 0.0106], ['op ', 0.0102],
        ['che', 0.0098], ['he ', 0.0094], ['ge ', 0.0090], ['ie ', 0.0086],
        ['ech', 0.0082], ['t e', 0.0078], ['uer', 0.0074], ['er ', 0.0070],
        ['r a', 0.0066], ['ass', 0.0062], ['ss ', 0.0058], ['am ', 0.0054],
        ['e g', 0.0050], ['ës ', 0.0046], ['s a', 0.0042], ['em ', 0.0038],
        ['un ', 0.0034], ['fir', 0.0030]
      ]),
      uniqueChars: new Set(['ä', 'é', 'ë']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('macedonian', {
      trigrams: new Map([
        ['на ', 0.0213], ['то ', 0.0197], ['та ', 0.0187], ['от ', 0.0176],
        ['е н', 0.0165], ['да ', 0.0154], ['ни ', 0.0143], ['те ', 0.0132],
        ['ја ', 0.0121], ['ат ', 0.0110], ['ите', 0.0106], ['во ', 0.0102],
        ['ата', 0.0098], ['ото', 0.0094], ['а н', 0.0090], ['а п', 0.0086],
        ['и н', 0.0082], ['и п', 0.0078], ['а с', 0.0074], ['о н', 0.0070],
        ['сто', 0.0066], ['ан ', 0.0062], ['ње ', 0.0058], ['ње', 0.0054],
        ['кој', 0.0050], ['тра', 0.0046], ['стр', 0.0042], ['а и', 0.0038],
        ['за ', 0.0034], ['пре', 0.0030]
      ]),
      uniqueChars: new Set(['ѓ', 'ѕ', 'ј', 'љ', 'њ', 'ќ', 'џ']),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('malagasy', {
      trigrams: new Map([
        ['ny ', 0.0213], ['ana', 0.0197], ['an\'', 0.0187], ['y n', 0.0176],
        ['ina', 0.0165], ['ami', 0.0154], ['sy ', 0.0143], ['a n', 0.0132],
        ['a a', 0.0121], ['aza', 0.0110], ['ny', 0.0106], ['y t', 0.0102],
        ['tsy', 0.0098], ['tra', 0.0094], ['ara', 0.0090], ['iz', 0.0086],
        ['zy', 0.0082], ['nan', 0.0078], ['ita', 0.0074], ['ay ', 0.0070],
        ['min', 0.0066], ['fan', 0.0062], ['dia', 0.0058], ['ia ', 0.0054],
        ['aza', 0.0050], ['m-p', 0.0046], ['ire', 0.0042], ['ren', 0.0038],
        ['ena', 0.0034], ['na ', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('malay', {
      trigrams: new Map([
        ['an ', 0.0213], ['ang', 0.0197], ['eng', 0.0187], ['kan', 0.0176],
        ['men', 0.0165], ['di ', 0.0154], ['ber', 0.0143], ['ada', 0.0132],
        ['yan', 0.0121], ['dan', 0.0110], ['pad', 0.0105], ['gan', 0.0101],
        ['nya', 0.0098], ['apa', 0.0095], ['lah', 0.0092], ['ala', 0.0089],
        ['itu', 0.0086], ['ran', 0.0083], ['dal', 0.0080], ['aya', 0.0077],
        ['ata', 0.0074], ['nga', 0.0071], ['bag', 0.0068], ['era', 0.0065],
        ['ala', 0.0062], ['pen', 0.0059], ['ega', 0.0056], ['say', 0.0053],
        ['mem', 0.0050], ['ung', 0.0047]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('malayalam', {
      trigrams: new Map([
        ['ിൽ', 0.0213], ['കൾ', 0.0197], ['ുന്ന', 0.0187], ['ത്ത', 0.0176],
        ['ന്ന', 0.0165], ['ൾ ', 0.0154], ['യും', 0.0143], ['ിന്', 0.0132],
        ['രു', 0.0121], ['തി', 0.0110], ['മല', 0.0106], ['ലയ', 0.0102],
        ['യാ', 0.0098], ['ാള', 0.0094], ['ളം', 0.0090], ['ം ', 0.0086],
        ['കേ', 0.0082], ['േര', 0.0078], ['രള', 0.0074], ['ളത', 0.0070],
        ['ത്', 0.0066], ['്ത', 0.0062], ['തി', 0.0058], ['ിൽ', 0.0054],
        ['ൽ ', 0.0050], ['ഒര', 0.0046], ['രു', 0.0042], ['ു ', 0.0038],
        ['സം', 0.0034], ['ംസ', 0.0030]
      ]),
      uniqueChars: new Set(['അ', 'ആ', 'ഇ', 'ഈ', 'ഉ', 'ഊ', 'ഋ', 'എ', 'ഏ', 'ഐ', 'ഒ', 'ഓ', 'ഔ', 'ക', 'ഖ', 'ഗ', 'ഘ', 'ങ', 'ച', 'ഛ', 'ജ', 'ഝ', 'ഞ', 'ട', 'ഠ', 'ഡ', 'ഢ', 'ണ', 'ത', 'ഥ', 'ദ', 'ധ', 'ന', 'പ', 'ഫ', 'ബ', 'ഭ', 'മ', 'യ', 'ര', 'റ', 'ല', 'ള', 'ഴ', 'വ', 'ശ', 'ഷ', 'സ', 'ഹ']),
      scriptPrimary: /[\u0D00-\u0D7F]/g
    });

    this._distributions.set('maltese', {
      trigrams: new Map([
        ['ta ', 0.0213], ['il-', 0.0197], ['li ', 0.0187], ['al ', 0.0176],
        ['ja ', 0.0165], ['jie', 0.0154], ['u l', 0.0143], ['iet', 0.0132],
        ['a t', 0.0121], ['jew', 0.0110], ['ew ', 0.0105], ['l-i', 0.0101],
        ['wa ', 0.0097], ['a j', 0.0094], ['all', 0.0091], ['ll-', 0.0088],
        ['l-m', 0.0085], ['mal', 0.0082], ['alt', 0.0079], ['lta', 0.0076],
        ['ta\'', 0.0073], ['\'', 0.0070], [' ma', 0.0067], ['ma ', 0.0064],
        ['a l', 0.0061], ['hu ', 0.0058], ['ma', 0.0055], ['a m', 0.0052],
        ['ha ', 0.0049], ['biż', 0.0046]
      ]),
      uniqueChars: new Set(['ċ', 'ġ', 'ħ', 'ż']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('maori', {
      trigrams: new Map([
        ['ki ', 0.0213], ['nga', 0.0197], ['te ', 0.0187], ['i t', 0.0176],
        ['ka ', 0.0165], ['ko ', 0.0154], ['ai ', 0.0143], ['ing', 0.0132],
        ['wha', 0.0121], ['ana', 0.0110], ['e t', 0.0106], ['ma ', 0.0102],
        ['me ', 0.0098], ['a t', 0.0094], ['he ', 0.0090], ['ha ', 0.0086],
        ['a k', 0.0082], ['o t', 0.0078], ['ia ', 0.0074], ['ria', 0.0070],
        ['ina', 0.0066], ['a m', 0.0062], ['ra ', 0.0058], ['a w', 0.0054],
        ['ga ', 0.0050], ['nā ', 0.0046], ['āor', 0.0042], ['ori', 0.0038],
        ['ri ', 0.0034], ['tou', 0.0030]
      ]),
      uniqueChars: new Set(['ā', 'ē', 'ī', 'ō', 'ū']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('marathi', {
      trigrams: new Map([
        ['च्य', 0.0213], ['या ', 0.0197], ['ला ', 0.0187], ['ाच', 0.0176],
        ['ार ', 0.0165], ['न्य', 0.0154], ['ामध', 0.0143], ['मध्', 0.0132],
        ['ध्य', 0.0121], ['या', 0.0110], ['ची ', 0.0105], ['त्य', 0.0100],
        ['ाच्', 0.0095], ['या', 0.0090], ['े त', 0.0085], ['चे ', 0.0080],
        ['ून ', 0.0075], ['क्त', 0.0070], ['ुले', 0.0065], ['ले ', 0.0060],
        ['ाल ', 0.0055], ['ाहि', 0.0050], ['हिज', 0.0045], ['जे ', 0.0040],
        ['संग', 0.0035], ['स्थ', 0.0030], ['त्र', 0.0025], ['ाय ', 0.0020],
        ['ोण ', 0.0015], ['कार', 0.0010]
      ]),
      uniqueChars: new Set(['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ', 'ए', 'ऐ', 'ओ', 'औ', 'क', 'ख', 'ग', 'घ', 'ङ', 'च', 'छ', 'ज', 'झ', 'ञ', 'ट', 'ठ', 'ड', 'ढ', 'ण', 'त', 'थ', 'द', 'ध', 'न', 'प', 'फ', 'ब', 'भ', 'म', 'य', 'र', 'ल', 'ळ', 'व', 'श', 'ष', 'स', 'ह']),
      scriptPrimary: /[\u0900-\u097F]/g
    });

    // this._distributions.set('mongolian', {
    //   trigrams: new Map([
    //     ['ын ', 0.0213], ['ийн', 0.0197], ['н х', 0.0187], ['ээр', 0.0176],
    //     ['ах ', 0.0165], ['ул ', 0.0154], ['уул', 0.0143], ['эн ', 0.0132],
    //     ['ан ', 0.0121], ['олг', 0.0110], ['лго', 0.0105], ['гол', 0.0101],
    //     ['ол ', 0.0097], ['л у', 0.0094], ['улс', 0.0091], ['лсы', 0.0088],
    //     ['сын', 0.0085], ['мон', 0.0082], ['онг', 0.0079], ['нго', 0.0076],
    //     ['гол', 0.0073], ['ол ', 0.0070], ['л у', 0.0067], ['улс', 0.0064],
    //     ['сын', 0.0061], ['ын ', 0.0058], ['н э', 0.0055], ['эрх', 0.0052],
    //     ['рхи', 0.0049], ['хий', 0.0046]
    //   ]),
    //   uniqueChars: new Set(['ө', 'ү', 'ц', 'ч', 'ш', 'э', 'ю', 'я']),
    //   scriptPrimary: /[\u0400-\u04FF]/g,
    //   scriptSecondary: /[\u1800-\u18AF]/g // For Traditional Mongolian script
    // });

    this._distributions.set('mongolian', {
      trigrams: new Map([
        // Common Mongolian trigrams
        ['ын ', 0.0213], ['ийн', 0.0197], ['н х', 0.0187], ['ээр', 0.0176],
        ['ах ', 0.0165], ['ул ', 0.0154], ['уул', 0.0143], ['эн ', 0.0132],
        ['ан ', 0.0121], ['олг', 0.0110], ['лго', 0.0105], ['гол', 0.0101],
        ['ол ', 0.0097], ['л у', 0.0094], ['улс', 0.0091], ['лсы', 0.0088],
        ['сын', 0.0085], ['мон', 0.0082], ['онг', 0.0079], ['нго', 0.0076],
        ['гол', 0.0073], ['ол ', 0.0070], ['л у', 0.0067], ['улс', 0.0064],
        ['сын', 0.0061], ['ын ', 0.0058], ['н э', 0.0055], ['эрх', 0.0052],
        ['рхи', 0.0049], ['хий', 0.0046],

        // Distinctive Mongolian trigrams
        ['мон', 0.0044], ['онг', 0.0042], ['нго', 0.0040], ['гол', 0.0038],
        ['ол ', 0.0036], ['л у', 0.0034], ['улс', 0.0032], ['сын', 0.0030],
        ['ын ', 0.0028], ['н б', 0.0026], ['баа', 0.0024], ['аат', 0.0022],
        ['тар', 0.0020], ['р у', 0.0018], ['ула', 0.0016], ['лаа', 0.0014],

        // Mongolian-specific patterns with distinctive letters
        ['өө', 0.0012], ['өөр', 0.0010], ['өрө', 0.0008], ['рөн', 0.0006],
        ['нхи', 0.0005], ['хий', 0.0004], ['йд ', 0.0003], ['д о', 0.0002],
        ['орн', 0.0001], ['рон', 0.00009], ['оны', 0.00008], ['ны ', 0.00007]
      ]),
      uniqueChars: new Set([
        // Distinctive Mongolian Cyrillic characters
        'ө', 'ү', 'ц', 'ч', 'ш', 'э', 'ю', 'я', 'Ө', 'Ү', 'Ц', 'Ч', 'Ш', 'Э', 'Ю', 'Я',
        // Other Cyrillic letters that help differentiate
        'ы', 'ь', 'ъ'
      ]),
      scriptPrimary: /[\u0400-\u04FF]/g,
      scriptSecondary: /[\u1800-\u18AF]/g // For Traditional Mongolian script
    });

    this._distributions.set('nepali', {
      trigrams: new Map([
        ['को ', 0.0213], ['मा ', 0.0197], ['हरू', 0.0187], ['अन', 0.0176],
        ['एको', 0.0165], ['ने ', 0.0154], ['र स', 0.0143], ['गर', 0.0132],
        ['पनि', 0.0121], ['न स', 0.0110], ['गर्', 0.0105], ['र्न', 0.0100],
        ['ले ', 0.0095], ['भए', 0.0090], ['हुन', 0.0085], ['छ ', 0.0080],
        ['ेको', 0.0075], ['नेप', 0.0070], ['ेपा', 0.0065], ['पाल', 0.0060],
        ['ाली', 0.0055], ['हुन', 0.0050], ['रुप', 0.0045], ['पम', 0.0040],
        ['मा ', 0.0035], ['देश', 0.0030], ['िक ', 0.0025], ['लाई', 0.0020],
        ['न्छ', 0.0015], ['छन्', 0.0010]
      ]),
      uniqueChars: new Set(['क', 'ख', 'ग', 'घ', 'ङ', 'च', 'छ', 'ज', 'झ', 'ञ', 'ट', 'ठ', 'ड', 'ढ', 'ण', 'त', 'थ', 'द', 'ध', 'न', 'प', 'फ', 'ब', 'भ', 'म', 'य', 'र', 'ल', 'व', 'श', 'ष', 'स', 'ह']),
      scriptPrimary: /[\u0900-\u097F]/g
    });

    this._distributions.set('norwegian', {
      trigrams: new Map([
        ['er ', 0.0213], ['en ', 0.0197], ['et ', 0.0187], ['for', 0.0176],
        ['det', 0.0165], ['ing', 0.0154], ['til', 0.0143], ['and', 0.0132],
        ['de ', 0.0121], ['som', 0.0110], ['ikk', 0.0105], ['kke', 0.0101],
        ['har', 0.0097], ['ene', 0.0094], ['seg', 0.0091], ['av ', 0.0088],
        ['på ', 0.0085], ['tte', 0.0082], ['ska', 0.0079], ['kan', 0.0076],
        ['med', 0.0073], ['vil', 0.0070], ['ett', 0.0067], ['ger', 0.0064],
        ['sjo', 0.0061], ['jeg', 0.0058], ['må ', 0.0055], ['opp', 0.0052],
        ['nge', 0.0049], ['lig', 0.0046]
      ]),
      uniqueChars: new Set(['æ', 'ø', 'å']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('oriya', {
      trigrams: new Map([
        ['ର ', 0.0213], ['କୁ ', 0.0197], ['ବା ', 0.0187], ['ତା ', 0.0176],
        ['ରେ ', 0.0165], ['ନ୍ତ', 0.0154], ['ର କ', 0.0143], ['ଏବ', 0.0132],
        ['ବଂ ', 0.0121], ['ମାନ', 0.0110], ['ର ସ', 0.0106], ['ସମ', 0.0102],
        ['ମର', 0.0098], ['ର ', 0.0094], ['ଓଡ଼', 0.0090], ['ଡ଼ିଆ', 0.0086],
        ['ିଆ', 0.0082], ['ଆ ', 0.0078], ['ଭା', 0.0074], ['ାଷ', 0.0070],
        ['ଷା', 0.0066], ['ା ', 0.0062], ['ର ଭ', 0.0058], ['ଭାର', 0.0054],
        ['ାରତ', 0.0050], ['ତ ', 0.0046], ['ର ଏ', 0.0042], ['ଏକ', 0.0038],
        ['କ ର', 0.0034], ['ରାଜ', 0.0030]
      ]),
      uniqueChars: new Set(['ଅ', 'ଆ', 'ଇ', 'ଈ', 'ଉ', 'ଊ', 'ଋ', 'ଌ', 'ଏ', 'ଐ', 'ଓ', 'ଔ', 'କ', 'ଖ', 'ଗ', 'ଘ', 'ଙ', 'ଚ', 'ଛ', 'ଜ', 'ଝ', 'ଞ', 'ଟ', 'ଠ', 'ଡ', 'ଢ', 'ଣ', 'ତ', 'ଥ', 'ଦ', 'ଧ', 'ନ', 'ପ', 'ଫ', 'ବ', 'ଭ', 'ମ', 'ଯ', 'ର', 'ଲ', 'ଳ', 'ଶ', 'ଷ', 'ସ', 'ହ', 'ଡ଼', 'ଢ଼']),
      scriptPrimary: /[\u0B00-\u0B7F]/g
    });

    // this._distributions.set('pashto', {
    //   trigrams: new Map([
    //     ['د ', 0.0213], ['په ', 0.0197], ['ده ', 0.0187], ['چې ', 0.0176],
    //     ['کې ', 0.0165], ['او ', 0.0154], ['ه د', 0.0143], ['ي د', 0.0132],
    //     ['نه ', 0.0121], ['ان ', 0.0110], ['تو ', 0.0106], ['و د', 0.0102],
    //     ['ه پ', 0.0098], ['پښ', 0.0094], ['ښت', 0.0090], ['تو', 0.0086],
    //     ['و ژ', 0.0082], ['ژب', 0.0078], ['به', 0.0074], ['ه ', 0.0070],
    //     ['ه ک', 0.0066], ['کښ', 0.0062], ['ښې', 0.0058], ['ې د', 0.0054],
    //     ['د ا', 0.0050], ['اف', 0.0046], ['فغ', 0.0042], ['غا', 0.0038],
    //     ['ان', 0.0034], ['نس', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ړ', 'ږ', 'ښ', 'څ', 'ځ', 'ډ', 'ټ', 'ۍ', 'ې', 'ي', 'ئ']),
    //   scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    // });

    this._distributions.set('pashto', {
      trigrams: new Map([
        // Common Pashto trigrams
        ['د ', 0.0213], ['په ', 0.0197], ['ده ', 0.0187], ['چې ', 0.0176],
        ['کې ', 0.0165], ['او ', 0.0154], ['ه د', 0.0143], ['ي د', 0.0132],
        ['نه ', 0.0121], ['ان ', 0.0110], ['تو ', 0.0106], ['و د', 0.0102],
        ['ه پ', 0.0098], ['پښ', 0.0094], ['ښت', 0.0090], ['تو', 0.0086],
        ['و ژ', 0.0082], ['ژب', 0.0078], ['به', 0.0074], ['ه ', 0.0070],
        ['ه ک', 0.0066], ['کښ', 0.0062], ['ښې', 0.0058], ['ې د', 0.0054],
        ['د ا', 0.0050], ['اف', 0.0046], ['فغ', 0.0042], ['غا', 0.0038],
        ['ان', 0.0034], ['نس', 0.0030],

        // Distinctively Pashto trigrams
        ['افغ', 0.0029], ['فغا', 0.0028], ['غان', 0.0027], ['انس', 0.0026],
        ['نست', 0.0025], ['ستا', 0.0024], ['تان', 0.0023], ['ښتو', 0.0022],
        ['تو ', 0.0021], ['و ژ', 0.0020], ['ژبه', 0.0019], ['به ', 0.0018],
        ['ړه ', 0.0017], ['ور ', 0.0016], ['ر پ', 0.0015], ['پښ', 0.0014],

        // Unique Pashto characters and combinations
        ['ړ', 0.0013], ['ږ', 0.0012], ['ڼ', 0.0011], ['ښ', 0.0010],
        ['څ', 0.0009], ['ځ', 0.0008], ['ډ', 0.0007], ['ټ', 0.0006],
        ['ۍ', 0.0005], ['ې', 0.0004], ['ۀ', 0.0003], ['ئ', 0.0002],
        ['ؤ', 0.0001]
      ]),
      uniqueChars: new Set([
        // Distinctive Pashto characters
        'ړ', 'ږ', 'ښ', 'څ', 'ځ', 'ډ', 'ټ', 'ۍ', 'ې', 'ی', 'ئ', 'ۀ',
        // Other distinctive characters
        '؟', '،', '٪'
      ]),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    });

    // this._distributions.set('persian', {
    //   trigrams: new Map([
    //     ['ان ', 0.0213], ['ای ', 0.0197], ['است', 0.0187], ['که ', 0.0176],
    //     ['را ', 0.0165], ['در ', 0.0154], ['های', 0.0143], ['ی ', 0.0132],
    //     ['ین ', 0.0121], ['به ', 0.0110], ['ند ', 0.0106], ['ده ', 0.0102],
    //     ['ود ', 0.0098], ['ار ', 0.0094], ['رد ', 0.0090], ['نی ', 0.0086],
    //     ['از ', 0.0082], ['ما ', 0.0078], ['ا ب', 0.0074], ['بر ', 0.0070],
    //     ['بر', 0.0066], ['رای', 0.0062], ['ای ', 0.0058], ['ی ا', 0.0054],
    //     ['ایر', 0.0050], ['ران', 0.0046], ['ان ', 0.0042], ['ن ا', 0.0038],
    //     ['اس', 0.0034], ['ست', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['آ', 'پ', 'چ', 'ژ', 'گ', 'ا', 'ب', 'ت', 'ث', 'ج', 'ح', 'خ', 'د', 'ذ', 'ر', 'ز', 'س', 'ش', 'ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ک', 'ل', 'م', 'ن', 'و', 'ه', 'ی']),
    //   scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    // });

    this._distributions.set('persian', {
      trigrams: new Map([
        // Common Persian trigrams - some overlap with Dari but different frequencies
        ['ان ', 0.0213], ['ای ', 0.0197], ['است', 0.0187], ['که ', 0.0176],
        ['را ', 0.0165], ['در ', 0.0154], ['های', 0.0143], ['ی ', 0.0132],
        ['ین ', 0.0121], ['به ', 0.0110], ['ند ', 0.0106], ['ده ', 0.0102],
        ['ود ', 0.0098], ['ار ', 0.0094], ['رد ', 0.0090], ['نی ', 0.0086],
        ['از ', 0.0082], ['ما ', 0.0078], ['ا ب', 0.0074], ['بر ', 0.0070],
        ['بر', 0.0066], ['رای', 0.0062], ['ای ', 0.0058], ['ی ا', 0.0054],
        ['ایر', 0.0050], ['ران', 0.0046], ['ان ', 0.0042], ['ن ا', 0.0038],
        ['اس', 0.0034], ['ست', 0.0030],

        // Distinctively Persian trigrams (vs Dari and Arabic)
        ['ایر', 0.0029], ['یرا', 0.0028], ['ران', 0.0027], ['انی', 0.0026],
        ['یی ', 0.0025], ['تهر', 0.0024], ['هرا', 0.0023], ['ن‌ه', 0.0022],
        ['‌ها', 0.0021], ['اصف', 0.0020], ['صفه', 0.0019], ['فها', 0.0018],
        ['هان', 0.0017], ['شیر', 0.0016], ['یرا', 0.0015], ['ازی', 0.0014],

        // Modern Persian internet/technical terms
        ['کام', 0.0013], ['امپ', 0.0012], ['مپی', 0.0011], ['پیو', 0.0010],
        ['یوت', 0.0009], ['وتر', 0.0008], ['می‌', 0.0007], ['‌شو', 0.0006],
        ['شود', 0.0005], ['ود.', 0.0004], ['د. ', 0.0003], ['وب‌', 0.0002],
        ['ب‌س', 0.0001]
      ]),
      uniqueChars: new Set([
        // Persian-specific characters
        'پ', 'چ', 'ژ', 'گ', 'ی', 'ک',
        // Distinctive Persian characters
        '‌', // Zero-width non-joiner (very common in Persian)
        '٪', '؟', '٫', '،'
      ]),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    });

    this._distributions.set('polish', {
      trigrams: new Map([
        ['nie', 0.0213], ['prz', 0.0197], ['rze', 0.0187], ['ach', 0.0176],
        ['ie ', 0.0165], ['ści', 0.0154], ['na ', 0.0143], ['go ', 0.0132],
        ['ej ', 0.0121], ['ows', 0.0110], ['ani', 0.0106], ['owa', 0.0103],
        ['ego', 0.0100], ['ch ', 0.0097], ['kie', 0.0094], ['cze', 0.0091],
        ['dzi', 0.0088], ['iej', 0.0085], ['sta', 0.0082], ['eni', 0.0079],
        ['eni', 0.0076], ['mi ', 0.0073], ['zes', 0.0070], ['do ', 0.0067],
        ['e p', 0.0064], ['ski', 0.0061], ['ą s', 0.0058], ['zie', 0.0055],
        ['łow', 0.0052], ['być', 0.0049]
      ]),
      uniqueChars: new Set(['ą', 'ć', 'ę', 'ł', 'ń', 'ó', 'ś', 'ź', 'ż']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('portuguese', {
      trigrams: new Map([
        ['de ', 0.0275], ['que', 0.0245], ['os ', 0.0221], ['as ', 0.0208],
        ['do ', 0.0203], ['da ', 0.0198], ['ão ', 0.0185], ['ara', 0.0178],
        ['ent', 0.0166], ['por', 0.0154], ['es ', 0.0147], ['ção', 0.0138],
        ['nte', 0.0134], ['com', 0.0129], ['to ', 0.0125], ['o d', 0.0122],
        ['ra ', 0.0118], ['não', 0.0114], ['men', 0.0110], ['um ', 0.0106],
        ['ão', 0.0102], ['tem', 0.0097], ['par', 0.0094], ['con', 0.0090],
        ['ade', 0.0086], ['a d', 0.0082], ['er ', 0.0077], ['ais', 0.0073],
        ['tra', 0.0070], ['se ', 0.0067],
        // Added distinctive Portuguese trigrams
        ['nha', 0.0065], ['lha', 0.0063], ['em ', 0.0061], ['ito', 0.0059],
        ['mas', 0.0057], ['uma', 0.0055], ['ões', 0.0053], ['nh', 0.0051]
      ]),
      // Expanded unique characters to better detect Portuguese
      uniqueChars: new Set([
        'ç', 'ã', 'õ',  // Very distinctive Portuguese characters
        'á', 'à', 'â', 'ê', 'í', 'ó', 'ú',  // Accented vowels
        'é', 'ô', 'è', 'ò'  // Additional accented vowels
      ]),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    // portuguese_brazil: {
    //   trigrams: new Map([
    //     ['os ', 0.0250], ['de ', 0.0240], ['que', 0.0230], ['da ', 0.0220],
    //     ['do ', 0.0210], ['as ', 0.0200], ['ar ', 0.0190], ['ção', 0.0180],
    //     ['es ', 0.0170], ['to ', 0.0160], ['ra ', 0.0150], ['ent', 0.0140],
    //     ['em ', 0.0130], ['nte', 0.0120], ['o d', 0.0110], ['a p', 0.0100],
    //     ['par', 0.0095], ['uma', 0.0090], ['ado', 0.0085], ['res', 0.0080],
    //     ['ão ', 0.0075], ['dos', 0.0070], ['ndo', 0.0065], ['men', 0.0060],
    //     ['o e', 0.0055], ['com', 0.0050], ['aça', 0.0045], ['des', 0.0040],
    //     ['nha', 0.0035], ['vel', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ç', 'ã', 'á', 'é', 'í', 'ó', 'ú', 'à', 'â', 'ê', 'ô', 'õ']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // },
    // portuguese_portugal: {
    //   trigrams: new Map([
    //     ['os ', 0.0250], ['de ', 0.0240], ['que', 0.0230], ['da ', 0.0220],
    //     ['do ', 0.0210], ['as ', 0.0200], ['ar ', 0.0190], ['ção', 0.0180],
    //     ['es ', 0.0170], ['to ', 0.0160], ['ra ', 0.0150], ['ent', 0.0140],
    //     ['em ', 0.0130], ['nte', 0.0120], ['o d', 0.0110], ['a p', 0.0100],
    //     ['par', 0.0095], ['uma', 0.0090], ['ado', 0.0085], ['res', 0.0080],
    //     ['ão ', 0.0075], ['dos', 0.0070], ['nto', 0.0065], ['men', 0.0060],
    //     ['o e', 0.0055], ['con', 0.0050], ['iss', 0.0045], ['des', 0.0040],
    //     ['ia ', 0.0035], ['ter', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ç', 'ã', 'á', 'é', 'í', 'ó', 'ú', 'à', 'â', 'ê', 'ô', 'õ']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // },

    this._distributions.set('punjabi', {
      trigrams: new Map([
        ['ਦੇ ', 0.0213], ['ਵਿੱ', 0.0197], ['ੱਚ ', 0.0187], ['ਦਾ ', 0.0176],
        ['ਦੀ ', 0.0165], ['ਨੂੰ ', 0.0154], ['ਦੀਆ', 0.0143], ['ਆਂ ', 0.0132],
        ['ਨਾਲ', 0.0121], ['ਹੈ ', 0.0110], ['ਪੰਜ', 0.0106], ['ੰਜਾ', 0.0102],
        ['ਜਾਬ', 0.0098], ['ਬੀ', 0.0094], ['ੀ ਭ', 0.0090], ['ਭਾ', 0.0086],
        ['ਾਸ਼', 0.0082], ['ਸ਼ਾ', 0.0078], ['ਾ ', 0.0074], ['ਵਿ', 0.0070],
        ['ਿਚ', 0.0066], ['ਚ ', 0.0062], ['ਹੈ', 0.0058], ['ੈ ', 0.0054],
        ['ਕਰ', 0.0050], ['ਰ ', 0.0046], ['ਦਾ', 0.0042], ['ਾ ', 0.0038],
        ['ਤੇ', 0.0034], ['ੇ ', 0.0030]
      ]),
      uniqueChars: new Set(['ਅ', 'ਆ', 'ਇ', 'ਈ', 'ਉ', 'ਊ', 'ਏ', 'ਐ', 'ਓ', 'ਔ', 'ਕ', 'ਖ', 'ਗ', 'ਘ', 'ਙ', 'ਚ', 'ਛ', 'ਜ', 'ਝ', 'ਞ', 'ਟ', 'ਠ', 'ਡ', 'ਢ', 'ਣ', 'ਤ', 'ਥ', 'ਦ', 'ਧ', 'ਨ', 'ਪ', 'ਫ', 'ਬ', 'ਭ', 'ਮ', 'ਯ', 'ਰ', 'ਲ', 'ਵ', 'ੜ', 'ਸ਼', 'ਸ', 'ਹ', 'ੑ', 'ਖ਼', 'ਗ਼', 'ਜ਼', 'ੜ੍ਹ', 'ਫ਼']),
      scriptPrimary: /[\u0A00-\u0A7F]/g
    });

    // this._distributions.set('romanian', {
    //   trigrams: new Map([
    //     ['ul ', 0.0213], ['re ', 0.0197], ['de ', 0.0187], ['și ', 0.0176],
    //     ['in ', 0.0165], ['la ', 0.0154], ['ale', 0.0143], ['ea ', 0.0132],
    //     ['ce ', 0.0121], ['lui', 0.0110], ['te ', 0.0106], ['ă î', 0.0102],
    //     ['în ', 0.0098], ['n r', 0.0094], ['rom', 0.0090], ['omâ', 0.0086],
    //     ['mân', 0.0082], ['âni', 0.0078], ['nia', 0.0074], ['ia ', 0.0070],
    //     ['car', 0.0066], ['are', 0.0062], ['re ', 0.0058], ['e a', 0.0054],
    //     ['au ', 0.0050], ['u f', 0.0046], ['fos', 0.0042], ['ost', 0.0038],
    //     ['st ', 0.0034], ['pen', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ă', 'â', 'î', 'ș', 'ț']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // });

    this._distributions.set('romanian', {
      trigrams: new Map([
        // Current high-frequency trigrams
        ['ul ', 0.0213], ['re ', 0.0197], ['de ', 0.0187], ['și ', 0.0176],
        ['in ', 0.0165], ['la ', 0.0154], ['ale', 0.0143], ['ea ', 0.0132],
        ['ce ', 0.0121], ['lui', 0.0110], ['te ', 0.0106], ['ă î', 0.0102],
        ['în ', 0.0098], ['n r', 0.0094], ['rom', 0.0090], ['omâ', 0.0086],
        ['mân', 0.0082], ['âni', 0.0078], ['nia', 0.0074], ['ia ', 0.0070],
        ['car', 0.0066], ['are', 0.0062], ['re ', 0.0058], ['e a', 0.0054],
        ['au ', 0.0050], ['u f', 0.0046], ['fos', 0.0042], ['ost', 0.0038],
        ['st ', 0.0034], ['pen', 0.0030],

        // Additional common Romanian trigrams
        ['că ', 0.0029], ['est', 0.0028], ['ste', 0.0027], ['tru', 0.0026],
        ['lor', 0.0025], ['une', 0.0024], ['ntă', 0.0023], ['con', 0.0022],
        ['par', 0.0021], ['sta', 0.0020], ['tre', 0.0019], ['pre', 0.0018],
        ['ele', 0.0017], ['cul', 0.0016], ['din', 0.0015], ['tat', 0.0014],
        ['ent', 0.0013], ['pri', 0.0012], ['eri', 0.0011], ['oar', 0.0010],

        // Distinctive Romanian trigrams
        ['ați', 0.0009], ['eșt', 0.0008], ['ții', 0.0007], ['țin', 0.0006],
        ['ăto', 0.0005], ['esc', 0.0004], ['cți', 0.0003], ['ășt', 0.0002],
        ['ica', 0.0001], ['eme', 0.00009], ['pro', 0.00008], ['ăți', 0.00007],
        ['ful', 0.00006], ['ntr', 0.00005], ['dre', 0.00004], ['unc', 0.00003],
        ['ură', 0.00002], ['șie', 0.00001]
      ]),
      uniqueChars: new Set(['ă', 'â', 'î', 'ș', 'ț', 'Ă', 'Â', 'Î', 'Ș', 'Ț']), // Added uppercase special characters
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('russian', {
      trigrams: new Map([
        ['ого', 0.0123], ['его', 0.0112], ['ени', 0.0098], ['ой', 0.0097],
        ['ост', 0.0094], ['ени', 0.0091], ['то ', 0.0089], ['ств', 0.0086],
        ['ние', 0.0083], ['ать', 0.0079], ['про', 0.0076], ['ет ', 0.0073],
        ['ия ', 0.0070], ['ать', 0.0067], ['но ', 0.0064], ['тся', 0.0061],
        ['ся ', 0.0058], ['ова', 0.0055], ['ый ', 0.0052], ['ров', 0.0049],
        ['как', 0.0046], ['ить', 0.0043], ['что', 0.0040], ['ные', 0.0037],
        ['ско', 0.0034], ['ым ', 0.0031], ['ана', 0.0028], ['тел', 0.0025],
        ['нно', 0.0022], ['аль', 0.0019]
      ]),
      uniqueChars: new Set(['щ', 'ъ', 'э', 'ю', 'я']),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('scottishGaelic', {
      trigrams: new Map([
        ['ach', 0.0312], ['adh', 0.0287], ['tha', 0.0214], ['eag', 0.0189],
        ['aig', 0.0175], ['ail', 0.0167], ['cha', 0.0159], ['ann', 0.0152],
        ['ean', 0.0148], ['air', 0.0145], ['idh', 0.0141], ['eil', 0.0138],
        ['inn', 0.0135], ['ead', 0.0132], ['ain', 0.0129], ['ich', 0.0126],
        ['bha', 0.0123], ['dh\'', 0.0120], ['eir', 0.0117], ['amh', 0.0114],
        ['ala', 0.0111], ['ise', 0.0108], ['neo', 0.0105], ['ear', 0.0102],
        ['chd', 0.0099], ['mha', 0.0096], ['ath', 0.0093], ['ith', 0.0090],
        ['ainn', 0.0087], ['bhf', 0.0084]
      ]),
      uniqueChars: new Set(['à', 'è', 'ì', 'ò', 'ù', '\'', 'ḃ', 'ḋ', 'ḟ', 'ġ', 'ṁ', 'ṗ', 'ṡ', 'ṫ']),
      scriptPrimary: /[A-Za-zÀ-ÖØ-öø-ÿḂḃḊḋḞḟĠġṀṁṖṗṠṡṪṫ]/g
    });

    this._distributions.set('serbian', {
      trigrams: new Map([
        ['је ', 0.0213], ['на ', 0.0197], ['ти ', 0.0187], ['и п', 0.0176],
        ['ост', 0.0165], ['ко ', 0.0154], ['ни ', 0.0143], ['да ', 0.0132],
        ['ва ', 0.0121], ['не ', 0.0110], ['ств', 0.0105], ['ног', 0.0101],
        ['ски', 0.0097], ['за ', 0.0094], ['ја ', 0.0091], ['ово', 0.0088],
        ['ања', 0.0085], ['ста', 0.0082], ['ије', 0.0079], ['их ', 0.0076],
        ['ред', 0.0073], ['при', 0.0070], ['ара', 0.0067], ['ка ', 0.0064],
        ['то ', 0.0061], ['ов ', 0.0058], ['про', 0.0055], ['бе ', 0.0052],
        ['одн', 0.0049], ['во ', 0.0046]
      ]),
      uniqueChars: new Set(['ј', 'љ', 'њ', 'ћ', 'џ']),
      scriptPrimary: /[\u0400-\u04FF]/g,
      scriptSecondary: /[A-Za-z\u00C0-\u00FF]/g // For Latin variant
    });

    this._distributions.set('sesotho', {
      trigrams: new Map([
        ['a h', 0.0213], ['ho ', 0.0197], ['le ', 0.0187], ['ng ', 0.0176],
        ['tsa', 0.0165], ['lo ', 0.0154], ['a b', 0.0143], ['ba ', 0.0132],
        ['a t', 0.0121], ['ka ', 0.0110], ['a l', 0.0106], ['la ', 0.0102],
        ['e l', 0.0098], ['e t', 0.0094], ['tla', 0.0090], ['o t', 0.0086],
        ['ana', 0.0082], ['na ', 0.0078], ['tha', 0.0074], ['ha ', 0.0070],
        ['mot', 0.0066], ['oth', 0.0062], ['tho', 0.0058], ['hlo', 0.0054],
        ['lol', 0.0050], ['ole', 0.0046], ['len', 0.0042], ['eng', 0.0038],
        ['g l', 0.0034], ['lok', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('shona', {
      trigrams: new Map([
        ['va ', 0.0213], ['ku ', 0.0197], ['iri', 0.0187], ['a k', 0.0176],
        ['ana', 0.0165], ['nga', 0.0154], ['ri ', 0.0143], ['chi', 0.0132],
        ['a v', 0.0121], ['na ', 0.0110], ['nda', 0.0106], ['da ', 0.0102],
        ['ka ', 0.0098], ['a m', 0.0094], ['mun', 0.0090], ['und', 0.0086],
        ['ndi', 0.0082], ['di ', 0.0078], ['a n', 0.0074], ['a c', 0.0070],
        ['cho', 0.0066], ['hon', 0.0062], ['ona', 0.0058], ['a z', 0.0054],
        ['zvi', 0.0050], ['vir', 0.0046], ['iro', 0.0042], ['ro ', 0.0038],
        ['cha', 0.0034], ['hik', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('sinhala', {
      trigrams: new Map([
        ['ය ', 0.0213], ['යේ ', 0.0197], ['ක් ', 0.0187], ['න්ද', 0.0176],
        ['න් ', 0.0165], ['කර', 0.0154], ['්ර', 0.0143], ['ට ', 0.0132],
        ['ත් ', 0.0121], ['ම ', 0.0110], ['සි', 0.0106], ['ිං', 0.0102],
        ['ංහ', 0.0098], ['හල', 0.0094], ['ල ', 0.0090], ['භ', 0.0086],
        ['ාෂ', 0.0082], ['ෂා', 0.0078], ['ාව', 0.0074], ['ව ', 0.0070],
        ['ශ්', 0.0066], ['්ර', 0.0062], ['රී', 0.0058], ['ී ල', 0.0054],
        ['ලං', 0.0050], ['ංක', 0.0046], ['කා', 0.0042], ['ාව', 0.0038],
        ['ව ', 0.0034], ['ශ්', 0.0030]
      ]),
      uniqueChars: new Set(['අ', 'ආ', 'ඇ', 'ඈ', 'ඉ', 'ඊ', 'උ', 'ඌ', 'ඍ', 'ඎ', 'ඏ', 'ඐ', 'එ', 'ඒ', 'ඓ', 'ඔ', 'ඕ', 'ඖ', 'ක', 'ඛ', 'ග', 'ඝ', 'ඞ', 'ඟ', 'ච', 'ඡ', 'ජ', 'ඣ', 'ඤ', 'ඥ', 'ඦ', 'ට', 'ඨ', 'ඩ', 'ඪ', 'ණ', 'ඬ', 'ත', 'ථ', 'ද', 'ධ', 'න', 'ඳ', 'ප', 'ඵ', 'බ', 'භ', 'ම', 'ඹ', 'ය', 'ර', 'ල', 'ව', 'ශ', 'ෂ', 'ස', 'හ', 'ළ', 'ෆ']),
      scriptPrimary: /[\u0D80-\u0DFF]/g
    });

    this._distributions.set('slovak', {
      trigrams: new Map([
        ['nie', 0.0213], ['ova', 0.0197], ['pre', 0.0187], ['ach', 0.0176],
        ['ie ', 0.0165], ['pri', 0.0154], ['sta', 0.0143], ['né ', 0.0132],
        ['ost', 0.0121], ['ho ', 0.0110], ['ní ', 0.0105], ['ého', 0.0101],
        ['ent', 0.0097], ['na ', 0.0094], ['a p', 0.0091], ['v p', 0.0088],
        ['pod', 0.0085], ['tom', 0.0082], ['ktor', 0.0079], ['ý p', 0.0076],
        ['rí ', 0.0073], ['aj ', 0.0070], ['ých', 0.0067], ['sti', 0.0064],
        ['kon', 0.0061], ['ale', 0.0058], ['čen', 0.0055], ['len', 0.0052],
        ['rov', 0.0049], ['vod', 0.0046]
      ]),
      uniqueChars: new Set(['á', 'ä', 'č', 'ď', 'é', 'í', 'ĺ', 'ľ', 'ň', 'ó', 'ŕ', 'š', 'ť', 'ú', 'ý', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('slovenian', {
      trigrams: new Map([
        ['je ', 0.0213], ['ne ', 0.0197], ['da ', 0.0187], ['in ', 0.0176],
        ['pri', 0.0165], ['na ', 0.0154], ['se ', 0.0143], ['jo ', 0.0132],
        ['za ', 0.0121], ['nje', 0.0110], ['eni', 0.0106], ['ni ', 0.0102],
        ['ti ', 0.0098], ['o s', 0.0094], ['ter', 0.0090], ['ki ', 0.0086],
        ['ven', 0.0082], ['enij', 0.0078], ['nij', 0.0074], ['ija', 0.0070],
        ['ja ', 0.0066], ['slo', 0.0062], ['lov', 0.0058], ['ove', 0.0054],
        ['ven', 0.0050], ['ens', 0.0046], ['nsk', 0.0042], ['ski', 0.0038],
        ['ki ', 0.0034], ['jez', 0.0030]
      ]),
      uniqueChars: new Set(['č', 'š', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('somali', {
      trigrams: new Map([
        ['ka ', 0.0213], ['aan', 0.0197], ['oo ', 0.0187], ['sha', 0.0176],
        ['ay ', 0.0165], ['ada', 0.0154], ['da ', 0.0143], ['an ', 0.0132],
        ['aya', 0.0121], ['yo ', 0.0110], ['a a', 0.0106], ['a s', 0.0102],
        ['som', 0.0098], ['oma', 0.0094], ['mal', 0.0090], ['ali', 0.0086],
        ['liy', 0.0082], ['iya', 0.0078], ['ya ', 0.0074], ['a i', 0.0070],
        ['in ', 0.0066], ['na ', 0.0062], ['o s', 0.0058], ['ah ', 0.0054],
        ['ha ', 0.0050], ['a k', 0.0046], ['ku ', 0.0042], ['u g', 0.0038],
        ['gey', 0.0034], ['eya', 0.0030]
      ]),
      uniqueChars: new Set(['c', 'x']), // Somali uses Latin alphabet but with unique sounds
      scriptPrimary: /[A-Za-z]/g
    });

    // this._distributions.set('spanish', {
    //   trigrams: new Map([
    //     ['que', 0.0411], ['de ', 0.0307], ['ión', 0.0215], ['la ', 0.0213],
    //     ['el ', 0.0205], ['en ', 0.0204], ['ent', 0.0191], ['con', 0.0155],
    //     ['par', 0.0145], ['est', 0.0145], ['ado', 0.0140], ['los', 0.0135],
    //     ['as ', 0.0132], ['nte', 0.0128], ['tra', 0.0123], ['por', 0.0120],
    //     ['ien', 0.0118], ['cia', 0.0115], ['cio', 0.0112], ['ara', 0.0109],
    //     ['es ', 0.0107], ['nto', 0.0104], ['una', 0.0101], ['res', 0.0098],
    //     ['te ', 0.0095], ['sta', 0.0092], ['do ', 0.0089], ['rec', 0.0086],
    //     ['com', 0.0083], ['das', 0.0080],
    //     // Adding Spanish-specific trigrams with higher weights
    //     [' mi ', 0.0180], ['mos ', 0.0170], ['ado ', 0.0165], ['cas', 0.0160],
    //     ['ico', 0.0155], ['yo ', 0.0150], ['pa ', 0.0145], ['jar', 0.0140],
    //     ['tas', 0.0135], ['tod', 0.0130], ['dad', 0.0125], ['ord', 0.0120]
    //   ]),
    //   uniqueChars: new Set(['ñ', 'á', 'é', 'í', 'ó', 'ú', 'ü', '¿', '¡']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    // });

    this._distributions.set('spanish', {
      trigrams: new Map([
        // Current high-frequency trigrams
        ['que', 0.0411], ['de ', 0.0307], ['ión', 0.0215], ['la ', 0.0213],
        ['el ', 0.0205], ['en ', 0.0204], ['ent', 0.0191], ['con', 0.0155],
        ['par', 0.0145], ['est', 0.0145], ['ado', 0.0140], ['los', 0.0135],
        ['as ', 0.0132], ['nte', 0.0128], ['tra', 0.0123], ['por', 0.0120],
        ['ien', 0.0118], ['cia', 0.0115], ['cio', 0.0112], ['ara', 0.0109],
        ['es ', 0.0107], ['nto', 0.0104], ['una', 0.0101], ['res', 0.0098],
        ['te ', 0.0095], ['sta', 0.0092], ['do ', 0.0089], ['rec', 0.0086],
        ['com', 0.0083], ['das', 0.0080],

        // Additional common Spanish trigrams
        ['del', 0.0078], ['ica', 0.0076], ['dad', 0.0074], ['and', 0.0072],
        ['ien', 0.0070], ['men', 0.0068], ['al ', 0.0066], ['ier', 0.0064],
        ['ora', 0.0062], ['a p', 0.0060], ['ndo', 0.0058], ['ra ', 0.0056],
        ['o d', 0.0054], ['e l', 0.0052], ['las', 0.0050], ['un ', 0.0048],

        // Distinctive Spanish trigrams
        ['ñ', 0.0046], ['ña', 0.0044], ['añ', 0.0042], ['ll', 0.0040],
        ['rr', 0.0038], ['uy', 0.0036], ['z ', 0.0034], ['gu', 0.0032],
        ['gue', 0.0030], ['gui', 0.0028], ['¿', 0.0026], ['¡', 0.0024],
        ['á', 0.0022], ['é', 0.0020], ['í', 0.0018], ['ó', 0.0016],
        ['ú', 0.0014], ['ü', 0.0012]
      ]),
      uniqueChars: new Set(['ñ', 'á', 'é', 'í', 'ó', 'ú', 'ü', '¿', '¡', 'Ñ', 'Á', 'É', 'Í', 'Ó', 'Ú', 'Ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('swahili', {
      trigrams: new Map([
        ['na ', 0.0213], ['wa ', 0.0197], ['ya ', 0.0187], ['a k', 0.0176],
        ['ku ', 0.0165], ['ni ', 0.0154], ['ka ', 0.0143], ['a m', 0.0132],
        ['li ', 0.0121], ['a n', 0.0110], ['i w', 0.0106], ['a w', 0.0102],
        ['ki ', 0.0098], ['hi ', 0.0094], ['swa', 0.0090], ['wah', 0.0086],
        ['ahi', 0.0082], ['hil', 0.0078], ['ili', 0.0074], ['li ', 0.0070],
        ['i n', 0.0066], ['nch', 0.0062], ['chi', 0.0058], ['i y', 0.0054],
        ['a y', 0.0050], ['yo ', 0.0046], ['lug', 0.0042], ['ugh', 0.0038],
        ['gha', 0.0034], ['ha ', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('swedish', {
      trigrams: new Map([
        ['en ', 0.0213], ['er ', 0.0197], ['att', 0.0187], ['och', 0.0176],
        ['för', 0.0165], ['ing', 0.0154], ['ar ', 0.0143], ['ett', 0.0132],
        ['det', 0.0121], ['som', 0.0110], ['den', 0.0105], ['är ', 0.0101],
        ['int', 0.0097], ['nte', 0.0094], ['ter', 0.0091], ['ill', 0.0088],
        ['ill', 0.0085], ['med', 0.0082], ['av ', 0.0079], ['han', 0.0076],
        ['and', 0.0073], ['jag', 0.0070], ['gen', 0.0067], ['lle', 0.0064],
        ['ska', 0.0061], ['kom', 0.0058], ['men', 0.0055], ['kan', 0.0052],
        ['var', 0.0049], ['upp', 0.0046]
      ]),
      uniqueChars: new Set(['å', 'ä', 'ö']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF]/g
    });

    this._distributions.set('tagalog', {
      trigrams: new Map([
        ['ang', 0.0213], ['ng ', 0.0197], ['an ', 0.0187], ['mga', 0.0176],
        ['sa ', 0.0165], ['na ', 0.0154], ['at ', 0.0143], ['ito', 0.0132],
        ['ay ', 0.0121], ['gal', 0.0110], ['alo', 0.0106], ['log', 0.0102],
        ['og ', 0.0098], ['g w', 0.0094], ['wik', 0.0090], ['ika', 0.0086],
        ['ka ', 0.0082], ['a n', 0.0078], ['a m', 0.0074], ['man', 0.0070],
        ['tag', 0.0066], ['aga', 0.0062], ['gal', 0.0058], ['alo', 0.0054],
        ['log', 0.0050], ['o a', 0.0046], ['ay ', 0.0042], ['y i', 0.0038],
        ['isa', 0.0034], ['san', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    this._distributions.set('tajik', {
      trigrams: new Map([
        ['дар', 0.0213], ['ро ', 0.0197], ['ои ', 0.0187], ['он ', 0.0176],
        ['аст', 0.0165], ['ан ', 0.0154], ['ба ', 0.0143], ['и д', 0.0132],
        ['да ', 0.0121], ['аро', 0.0110], ['тоҷ', 0.0106], ['оҷи', 0.0102],
        ['ҷик', 0.0098], ['ики', 0.0094], ['кис', 0.0090], ['ист', 0.0086],
        ['сто', 0.0082], ['тон', 0.0078], ['он ', 0.0074], ['н м', 0.0070],
        ['меб', 0.0066], ['еба', 0.0062], ['баш', 0.0058], ['аша', 0.0054],
        ['шад', 0.0050], ['ад', 0.0046], ['д к', 0.0042], ['ки ', 0.0038],
        ['и з', 0.0034], ['заб', 0.0030]
      ]),
      uniqueChars: new Set(['ғ', 'ӣ', 'қ', 'ӯ', 'ҷ', 'ҳ']),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('tamil', {
      trigrams: new Map([
        ['ில்', 0.0213], ['கள்', 0.0197], ['ும் ', 0.0187], ['ரு ', 0.0176],
        ['அவ', 0.0165], ['ான', 0.0154], ['ார்', 0.0143], ['க்க', 0.0132],
        ['ன்', 0.0121], ['ைய', 0.0110], ['தம', 0.0106], ['மிழ', 0.0102],
        ['ிழ்', 0.0098], ['ழ் ', 0.0094], ['ொ', 0.0090], ['மொ', 0.0086],
        ['ொழ', 0.0082], ['ழி', 0.0078], ['ியா', 0.0074], ['யாக', 0.0070],
        ['கம', 0.0066], ['ம் ', 0.0062], ['உள', 0.0058], ['ளத', 0.0054],
        ['து', 0.0050], ['ின்', 0.0046], ['ன்ற', 0.0042], ['றி', 0.0038],
        ['ிய', 0.0034], ['யா', 0.0030]
      ]),
      uniqueChars: new Set(['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ', 'ஃ', 'க', 'ங', 'ச', 'ஞ', 'ட', 'ண', 'த', 'ந', 'ப', 'ம', 'ய', 'ர', 'ல', 'வ', 'ழ', 'ள', 'ற', 'ன', 'ஜ', 'ஷ', 'ஸ', 'ஹ']),
      scriptPrimary: /[\u0B80-\u0BFF]/g
    });
    //
    // this._distributions.set('tatar', {
    //   trigrams: new Map([
    //     ['лар', 0.0213], ['нда', 0.0197], ['ның', 0.0187], ['да ', 0.0176],
    //     ['лар', 0.0165], ['ан ', 0.0154], ['ен ', 0.0143], ['тан', 0.0132],
    //     ['ләр', 0.0121], ['н б', 0.0110], ['бу ', 0.0105], ['ул ', 0.0101],
    //     ['л т', 0.0097], ['тат', 0.0094], ['тар', 0.0091], ['арс', 0.0088],
    //     ['рст', 0.0085], ['ста', 0.0082], ['тан', 0.0079], ['ан ', 0.0076],
    //     ['н т', 0.0073], ['тел', 0.0070], ['еле', 0.0067], ['ле ', 0.0064],
    //     ['е б', 0.0061], ['бар', 0.0058], ['ары', 0.0055], ['ы б', 0.0052],
    //     ['бел', 0.0049], ['елә', 0.0046]
    //   ]),
    //   uniqueChars: new Set(['ә', 'ө', 'ү', 'җ', 'ң', 'һ']),
    //   scriptPrimary: /[\u0400-\u04FF]/g
    // });

    this._distributions.set('tatar', {
      trigrams: new Map([
        // Common Tatar trigrams
        ['лар', 0.0213], ['нда', 0.0197], ['ның', 0.0187], ['да ', 0.0176],
        ['лар', 0.0165], ['ан ', 0.0154], ['ен ', 0.0143], ['тан', 0.0132],
        ['ләр', 0.0121], ['н б', 0.0110], ['бу ', 0.0105], ['ул ', 0.0101],
        ['л т', 0.0097], ['тат', 0.0094], ['тар', 0.0091], ['арс', 0.0088],
        ['рст', 0.0085], ['ста', 0.0082], ['тан', 0.0079], ['ан ', 0.0076],
        ['н т', 0.0073], ['тел', 0.0070], ['еле', 0.0067], ['ле ', 0.0064],
        ['е б', 0.0061], ['бар', 0.0058], ['ары', 0.0055], ['ы б', 0.0052],
        ['бел', 0.0049], ['елә', 0.0046],

        // Distinctive Tatar trigrams
        ['тат', 0.0044], ['ата', 0.0042], ['тар', 0.0040], ['арс', 0.0038],
        ['рст', 0.0036], ['ста', 0.0034], ['тан', 0.0032], ['анд', 0.0030],
        ['нда', 0.0028], ['да ', 0.0026], ['а к', 0.0024], ['каз', 0.0022],
        ['аза', 0.0020], ['зан', 0.0018], ['нь ', 0.0016], ['ь т', 0.0014],

        // Tatar-specific patterns with distinctive letters
        ['әкә', 0.0012], ['кәш', 0.0010], ['шеф', 0.0008], ['фкә', 0.0006],
        ['һәм', 0.0005], ['өч', 0.0004], ['чен', 0.0003], ['нче', 0.0002],
        ['җан', 0.0001], ['ңа ', 0.00009], ['ү б', 0.00008], ['бүл', 0.00007]
      ]),
      uniqueChars: new Set([
        // Distinctive Tatar Cyrillic characters
        'ә', 'ө', 'ү', 'җ', 'ң', 'һ', 'Ә', 'Ө', 'Ү', 'Җ', 'Ң', 'Һ',
        // Other Cyrillic letters that help differentiate from non-Cyrillic Turkic languages
        'ь', 'ъ', 'э', 'ы'
      ]),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('telugu', {
      trigrams: new Map([
        ['ిన', 0.0213], ['ున', 0.0197], ['కు ', 0.0187], ['లో ', 0.0176],
        ['ను ', 0.0165], ['ాల', 0.0154], ['లు ', 0.0143], ['ాన', 0.0132],
        ['కున', 0.0121], ['తో ', 0.0110], ['తెల', 0.0106], ['ెలు', 0.0102],
        ['లుగ', 0.0098], ['ుగు', 0.0094], ['గు ', 0.0090], ['భాష', 0.0086],
        ['ాష', 0.0082], ['ష ', 0.0078], ['ని ', 0.0074], ['రు ', 0.0070],
        ['క్క', 0.0066], ['్కడ', 0.0062], ['డ ', 0.0058], ['మా', 0.0054],
        ['ాట', 0.0050], ['టల', 0.0046], ['లాడ', 0.0042], ['ాడు', 0.0038],
        ['డుత', 0.0034], ['ుతా', 0.0030]
      ]),
      uniqueChars: new Set(['అ', 'ఆ', 'ఇ', 'ఈ', 'ఉ', 'ఊ', 'ఋ', 'ఎ', 'ఏ', 'ఐ', 'ఒ', 'ఓ', 'ఔ', 'క', 'ఖ', 'గ', 'ఘ', 'ఙ', 'చ', 'ఛ', 'జ', 'ఝ', 'ఞ', 'ట', 'ఠ', 'డ', 'ఢ', 'ణ', 'త', 'థ', 'ద', 'ధ', 'న', 'ప', 'ఫ', 'బ', 'భ', 'మ', 'య', 'ర', 'ఱ', 'ల', 'ళ', 'వ', 'శ', 'ష', 'స', 'హ']),
      scriptPrimary: /[\u0C00-\u0C7F]/g
    });

    this._distributions.set('thai', {
      trigrams: new Map([
        ['การ', 0.0213], ['ที่', 0.0197], ['ไป', 0.0187], ['ของ', 0.0176],
        ['ไม่', 0.0165], ['ให้', 0.0154], ['ใน', 0.0143], ['เป็น', 0.0132],
        ['าง ', 0.0121], ['ด้วย', 0.0110], ['ภาษ', 0.0106], ['าษา', 0.0102],
        ['ษาไ', 0.0098], ['าไท', 0.0094], ['ไทย', 0.0090], ['ย ', 0.0086],
        ['เป็', 0.0082], ['ป็น', 0.0078], ['็นภ', 0.0074], ['นภา', 0.0070],
        ['าษา', 0.0066], ['ษาร', 0.0062], ['ราช', 0.0058], ['าชก', 0.0054],
        ['ชกา', 0.0050], ['การ', 0.0046], ['าร', 0.0042], ['รขอ', 0.0038],
        ['ของ', 0.0034], ['องป', 0.0030]
      ]),
      uniqueChars: new Set(['ก', 'ข', 'ค', 'ฆ', 'ง', 'จ', 'ฉ', 'ช', 'ซ', 'ฌ', 'ญ', 'ฎ', 'ฏ', 'ฐ', 'ฑ', 'ฒ', 'ณ', 'ด', 'ต', 'ถ', 'ท', 'ธ', 'น', 'บ', 'ป', 'ผ', 'ฝ', 'พ', 'ฟ', 'ภ', 'ม', 'ย', 'ร', 'ล', 'ว', 'ศ', 'ษ', 'ส', 'ห', 'ฬ', 'อ', 'ฮ', 'ฤ', 'ฦ', 'ะ', 'ั', 'า', 'ำ', 'ิ', 'ี', 'ึ', 'ื', 'ุ', 'ู', 'เ', 'แ', 'โ', 'ใ', 'ไ', '็', '่', '้', '๊', '๋', '์']),
      scriptPrimary: /[\u0E00-\u0E7F]/g
    });

    this._distributions.set('tibetan', {
      trigrams: new Map([
        ['གི ', 0.0213], ['ན་', 0.0197], ['ར་', 0.0187], ['འི་', 0.0176],
        ['ས་', 0.0165], ['ད་', 0.0154], ['ི་', 0.0143], ['ང་', 0.0132],
        ['མི་', 0.0121], ['ིན་', 0.0110], ['བོད', 0.0106], ['ོད་', 0.0102],
        ['ད་ས', 0.0098], ['་སྐ', 0.0094], ['སྐད', 0.0090], ['ད་', 0.0086],
        ['་ནི', 0.0082], ['ནི་', 0.0078], ['ི་བ', 0.0074], ['་བོ', 0.0070],
        ['བོད', 0.0066], ['ོད་', 0.0062], ['ད་ཡ', 0.0058], ['་ཡི', 0.0054],
        ['ཡིག', 0.0050], ['ིག་', 0.0046], ['ག་ཡ', 0.0042], ['་ཡི', 0.0038],
        ['ཡིན', 0.0034], ['ིན་', 0.0030]
      ]),
      uniqueChars: new Set(['ཀ', 'ཁ', 'ག', 'ང', 'ཅ', 'ཆ', 'ཇ', 'ཉ', 'ཏ', 'ཐ', 'ད', 'ན', 'པ', 'ཕ', 'བ', 'མ', 'ཙ', 'ཚ', 'ཛ', 'ཝ', 'ཞ', 'ཟ', 'འ', 'ཡ', 'ར', 'ལ', 'ཤ', 'ས', 'ཧ', 'ཨ', '་', 'ཱ', 'ི', 'ུ', 'ེ', 'ོ', 'ཾ', 'ཿ', '྄', 'ྐ', 'ྒ', 'ྔ', 'ྕ', 'ྗ', 'ྙ', 'ྟ', 'ྡ', 'ྣ', 'ྤ', 'ྦ', 'ྨ', 'ྩ', 'ྫ', 'ྭ', 'ྱ', 'ྲ', 'ླ', 'ྷ', 'ྺ']),
      scriptPrimary: /[\u0F00-\u0FFF]/g
    });

    this._distributions.set('tigrinya', {
      trigrams: new Map([
        ['ናይ', 0.0213], ['ኢዩ', 0.0197], ['ን ', 0.0187], ['ዋ ', 0.0176],
        ['ይ ', 0.0165], ['ታ ', 0.0154], ['ዎም', 0.0143], ['ኣለ', 0.0132],
        ['ሎም', 0.0121], ['ም ኣ', 0.0110], ['ተ', 0.0106], ['ትግ', 0.0102],
        ['ግር', 0.0098], ['ርኛ', 0.0094], ['ኛ ', 0.0090], ['ቋን', 0.0086],
        ['ንቋ', 0.0082], ['ቋ ', 0.0078], ['ናይ', 0.0074], ['ይ ኢ', 0.0070],
        ['ኢት', 0.0066], ['ትዮ', 0.0062], ['ዮጵ', 0.0058], ['ጵያ', 0.0054],
        ['ያ ', 0.0050], ['ነው', 0.0046], ['ውር', 0.0042], ['ርነ', 0.0038],
        ['ነት', 0.0034], ['ት ', 0.0030]
      ]),
      uniqueChars: new Set(['ሀ', 'ለ', 'ሐ', 'መ', 'ሠ', 'ረ', 'ሰ', 'ሸ', 'ቀ', 'በ', 'ተ', 'ቸ', 'ኀ', 'ነ', 'ኘ', 'አ', 'ከ', 'ኸ', 'ወ', 'ዐ', 'ዘ', 'ژ', 'የ', 'ደ', 'ጀ', 'ገ', 'ጠ', 'ጨ', 'ጰ', 'ጸ', 'ፀ', 'ፈ', 'ፐ']),
      scriptPrimary: /[\u1200-\u137F]/g
    });

    // this._distributions.set('turkish', {
    //   trigrams: new Map([
    //     ['lar', 0.0148], ['bir', 0.0142], ['ini', 0.0138], ['nde', 0.0128],
    //     ['ler', 0.0126], ['ara', 0.0123], ['dan', 0.0121], ['in ', 0.0119],
    //     ['er ', 0.0115], ['dir', 0.0112], ['en ', 0.0108], ['ile', 0.0104],
    //     ['esi', 0.0101], ['rin', 0.0098], ['de ', 0.0096], ['inde', 0.0094],
    //     ['nda', 0.0092], ['sı ', 0.0090], ['an ', 0.0087], ['e b', 0.0084],
    //     ['a b', 0.0081], ['da ', 0.0078], ['ün ', 0.0075], ['tür', 0.0072],
    //     ['ürk', 0.0069], ['rkç', 0.0066], ['çe ', 0.0063], ['dil', 0.0060],
    //     ['ili', 0.0057], ['li ', 0.0054]
    //   ]),
    //   uniqueChars: new Set(['ç', 'ğ', 'ı', 'ö', 'ş', 'ü']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u011E\u011F\u0130\u0131\u015E\u015F]/g
    // });

    this._distributions.set('turkish', {  trigrams: new Map([
        ['lar', 0.0213], ['ar ', 0.0197], ['bir', 0.0187], ['in ', 0.0176],
        ['er ', 0.0165], ['da ', 0.0154], ['nda', 0.0143], ['de ', 0.0132],
        ['en ', 0.0121], ['ler', 0.0110], ['ir ', 0.0105], ['an ', 0.0100],
        ['ın ', 0.0096], ['ara', 0.0092], ['and', 0.0088], ['ini', 0.0084],
        ['nı ', 0.0080], ['le ', 0.0076], ['eri', 0.0072], ['yor', 0.0068],
        ['ası', 0.0064], ['arı', 0.0060], ['ile', 0.0056], ['dan', 0.0052],
        ['eki', 0.0048], ['den', 0.0044], ['ana', 0.0040], ['n b', 0.0036],
        ['dır', 0.0032], ['ını', 0.0028], ['aya', 0.0027], ['iye', 0.0026],
        ['eti', 0.0025], ['li ', 0.0024], ['ki ', 0.0023], ['aya', 0.0022],
        ['ber', 0.0021], ['ece', 0.0020], ['tir', 0.0019], ['nde', 0.0018],
        ['iye', 0.0017], ['ard', 0.0016], ['yet', 0.0015], ['na ', 0.0014],
        ['esi', 0.0013], ['sin', 0.0012], ['ola', 0.0011], ['edi', 0.0010],
        ['aca', 0.0009], ['çin', 0.0008], ['ğı ', 0.0007], ['şı ', 0.0006],
        ['ürk', 0.0005], ['öyl', 0.0004], ['ğın', 0.0003], ['çok', 0.0002],
        ['ıyo', 0.0001], ['ünü', 0.00009], ['ğim', 0.00008], ['şti', 0.00007],
        ['ğer', 0.00006], ['ğru', 0.00005], ['için', 0.00004], ['çek', 0.00003],
        ['şek', 0.00002], ['öne', 0.00001], ['üze', 0.000009], ['şun', 0.000008],
        ['türk', 0.000007], ['şey', 0.000006], ['öyle', 0.000005], ['ünkü', 0.000004],
        ['çük', 0.000003], ['ğlu', 0.000002], ['şka', 0.000001]]),
      uniqueChars: new Set(['ç', 'ğ', 'ı', 'İ', 'ö', 'ş', 'ü', 'Ç', 'Ğ', 'I', 'Ö', 'Ş', 'Ü']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('turkmen', {
      trigrams: new Map([
        ['lar', 0.0213], ['da ', 0.0197], ['yn ', 0.0187], ['an ', 0.0176],
        ['nda', 0.0165], ['ýn ', 0.0154], ['ar ', 0.0143], ['we ', 0.0132],
        ['en ', 0.0121], ['bir', 0.0110], ['dir', 0.0106], ['men', 0.0102],
        ['tür', 0.0098], ['ürk', 0.0094], ['rkm', 0.0090], ['kme', 0.0086],
        ['men', 0.0082], ['en ', 0.0078], ['n d', 0.0074], ['dil', 0.0070],
        ['ili', 0.0066], ['li ', 0.0062], ['ter', 0.0058], ['erj', 0.0054],
        ['rji', 0.0050], ['jim', 0.0046], ['ime', 0.0042], ['e e', 0.0038],
        ['ed', 0.0034], ['dil', 0.0030]
      ]),
      uniqueChars: new Set(['ä', 'ň', 'ö', 'ş', 'ü', 'ý', 'ž']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('ukrainian', {
      trigrams: new Map([
        ['ого', 0.0112], ['ння', 0.0104], ['ої', 0.0099], ['на ', 0.0091],
        ['ти ', 0.0088], ['ого', 0.0087], ['енн', 0.0083], ['про', 0.0082],
        ['ть ', 0.0079], ['ськ', 0.0075], ['го ', 0.0072], ['вид', 0.0069],
        ['пер', 0.0066], ['ува', 0.0063], ['ан ', 0.0060], ['ві ', 0.0057],
        ['ни ', 0.0054], ['від', 0.0051], ['вал', 0.0048], ['ід ', 0.0045],
        ['ий ', 0.0042], ['ися', 0.0039], ['ами', 0.0036], ['ові', 0.0033],
        ['ком', 0.0030], ['оро', 0.0027], ['авл', 0.0024], ['іст', 0.0021],
        ['іль', 0.0018], ['єть', 0.0015]
      ]),
      uniqueChars: new Set(['є', 'ї', 'і', 'ґ']),
      scriptPrimary: /[\u0400-\u04FF]/g
    });

    this._distributions.set('urdu', {
      trigrams: new Map([
        ['کے ', 0.0213], ['میں', 0.0197], ['اور', 0.0187], ['ہے ', 0.0176],
        ['کی ', 0.0165], ['سے ', 0.0154], ['کو ', 0.0143], ['نے ', 0.0132],
        ['کہ ', 0.0121], ['ہیں', 0.0110], ['ں ک', 0.0106], ['ان ', 0.0102],
        ['ا ک', 0.0098], ['ر ا', 0.0094], ['ن ک', 0.0090], ['ہ ا', 0.0086],
        ['ہ ک', 0.0082], ['ی ا', 0.0078], ['تھ', 0.0074], ['ھا', 0.0070],
        ['ا ہ', 0.0066], ['ں ا', 0.0062], ['جا', 0.0058], ['اس', 0.0054],
        ['س ک', 0.0050], ['گی', 0.0046], ['یا', 0.0042], ['ا ج', 0.0038],
        ['رد', 0.0034], ['دو', 0.0030]
      ]),
      uniqueChars: new Set(['ٹ', 'ڈ', 'ڑ', 'ں', 'ہ', 'ے']),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    });

    this._distributions.set('vietnamese', {
      trigrams: new Map([
        ['ng ', 0.0213], ['ông', 0.0197], ['của', 0.0187], ['là ', 0.0176],
        ['một', 0.0165], ['các', 0.0154], ['cho', 0.0143], ['những', 0.0132],
        ['trong', 0.0121], ['và ', 0.0110], ['iệt', 0.0106], ['ệt ', 0.0102],
        ['t n', 0.0098], ['nam', 0.0094], ['am ', 0.0090], ['tiế', 0.0086],
        ['iến', 0.0082], ['ếng', 0.0078], ['g v', 0.0074], ['vie', 0.0070],
        ['iệt', 0.0066], ['ệt', 0.0062], ['t n', 0.0058], ['nam', 0.0054],
        ['am', 0.0050], ['n n', 0.0046], ['ngô', 0.0042], ['gôn', 0.0038],
        ['ôn ', 0.0034], ['n n', 0.0030]
      ]),
      uniqueChars: new Set(['ă', 'â', 'đ', 'ê', 'ô', 'ơ', 'ư', 'ả', 'ẻ', 'ẽ', 'ỉ', 'ỏ', 'ủ', 'ử', 'ẳ', 'ẵ', 'ẹ', 'ệ', 'ỵ', 'ỷ', 'ỹ', 'ắ', 'ằ', 'ặ', 'ẹ', 'ẻ', 'ẽ', 'ế', 'ề', 'ể', 'ễ', 'ệ', 'ố', 'ồ', 'ổ', 'ỗ', 'ộ', 'ớ', 'ờ', 'ở', 'ỡ', 'ợ', 'ứ', 'ừ', 'ử', 'ữ', 'ự']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    this._distributions.set('welsh', {
      trigrams: new Map([
        ['yn ', 0.0213], ['dd ', 0.0197], ['ydd', 0.0187], ['yr ', 0.0176],
        ['edd', 0.0165], ['an ', 0.0154], ['ddi', 0.0143], ['gyda', 0.0132],
        ['eth', 0.0121], ['wyd', 0.0110], ['cy', 0.0106], ['cym', 0.0102],
        ['ymr', 0.0098], ['mra', 0.0094], ['rae', 0.0090], ['aeg', 0.0086],
        ['eg ', 0.0082], ['g i', 0.0078], ['iai', 0.0074], ['aith', 0.0070],
        ['th ', 0.0066], ['i g', 0.0062], ['gae', 0.0058], ['ael', 0.0054],
        ['el ', 0.0050], ['od ', 0.0046], ['de ', 0.0042], ['yf', 0.0038],
        ['fod', 0.0034], ['d y', 0.0030]
      ]),
      uniqueChars: new Set(['â', 'ê', 'î', 'ô', 'û', 'ŵ', 'ŷ']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    });

    // this._distributions.set('yoruba', {
    //   trigrams: new Map([
    //     ['ní ', 0.0213], ['ọn ', 0.0197], ['àn ', 0.0187], ['ẹ̀ ', 0.0176],
    //     ['ti ', 0.0165], ['kí ', 0.0154], ['jẹ́', 0.0143], ['lọ ', 0.0132],
    //     ['wọ́', 0.0121], ['sì ', 0.0110], ['yor', 0.0106], ['orù', 0.0102],
    //     ['rùb', 0.0098], ['bá', 0.0094], ['á ', 0.0090], ['èdè', 0.0086],
    //     ['dè ', 0.0082], ['e y', 0.0078], ['yor', 0.0074], ['orù', 0.0070],
    //     ['rùb', 0.0066], ['bá', 0.0062], ['á ', 0.0058], ['jẹ́', 0.0054],
    //     ['ẹ́ ', 0.0050], ['èd', 0.0046], ['dè ', 0.0042], ['kan', 0.0038],
    //     ['an ', 0.0034], ['ní ', 0.0030]
    //   ]),
    //   uniqueChars: new Set(['ẹ', 'ọ', 'ṣ', 'à', 'á', 'è', 'é', 'ì', 'í', 'ò', 'ó', 'ù', 'ú']),
    //   scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u017F]/g
    // });


    this._distributions.set('yoruba', {  trigrams: new Map([
        ['ati', 0.0213], ['nin', 0.0197], ['ọn ', 0.0187], ['ti ', 0.0176],
        ['an ', 0.0165], ['in ', 0.0154], ['ún ', 0.0143], ['ọ́n', 0.0132],
        ['àn ', 0.0121], ['àti', 0.0110], ['ni ', 0.0105], ['ọ̀n', 0.0100],
        ['kan', 0.0096], ['tí ', 0.0092], ['ìn ', 0.0088], ['ọ́ ', 0.0084],
        ['ìyẹ', 0.0080], ['ọjọ', 0.0076], ['gbé', 0.0072], ['ẹ̀n', 0.0068],
        ['ìgb', 0.0064], ['ọlọ', 0.0060], ['àwọ', 0.0056], ['yìn', 0.0052],
        ['ọmọ', 0.0048], ['gbà', 0.0044], ['bá ', 0.0040], ['kọ́', 0.0036],
        ['lọ ', 0.0032], ['sí ', 0.0028], ['ọ̀ ', 0.0027], ['kí ', 0.0026],
        ['lé ', 0.0025], ['bọ̀', 0.0024], ['adé', 0.0023], ['ẹni', 0.0022],
        ['jẹ́', 0.0021], ['ọrọ', 0.0020], ['wọn', 0.0019], ['nítorí', 0.0018],
        ['ilẹ̀', 0.0017], ['wà ', 0.0016], ['pé ', 0.0015], ['òun', 0.0014],
        ['ọjà', 0.0013], ['jọ ', 0.0012], ['èdè', 0.0011], ['ọwọ́', 0.0010],
        ['ọkọ', 0.0009], ['ẹ̀', 0.0008], ['rẹ̀ ', 0.0007], ['ìwé', 0.0006],
        ['orí', 0.0005], ['àwa', 0.0004], ['ọ̀rẹ́', 0.0003], ['ọ̀gá', 0.0002],
        ['ọ̀dọ́', 0.0001], ['ẹ̀kọ́', 0.00009], ['gbọ́', 0.00008], ['ìbí', 0.00007],
        ['ìlú', 0.00006], ['òkú', 0.00005], ['òde', 0.00004], ['olá', 0.00003],
        ['odò', 0.00002], ['onà', 0.00001], ['ẹbí', 0.000009], ['ìṣẹ́', 0.000008],
        ['ẹgbẹ́', 0.000007], ['ìdí', 0.000006], ['ọkàn', 0.000005], ['ọ̀nà', 0.000004],
        ['ẹ̀mí', 0.000003], ['ọ̀gọ̀', 0.000002], ['ẹ̀bí', 0.000001]]),
      uniqueChars: new Set(['à', 'á', 'è', 'é', 'ẹ', 'ẹ̀', 'ẹ́', 'ì', 'í', 'ò', 'ó', 'ọ', 'ọ̀', 'ọ́', 'ù', 'ú', 'À', 'Á', 'È', 'É', 'Ẹ', 'Ẹ̀', 'Ẹ́', 'Ì', 'Í', 'Ò', 'Ó', 'Ọ', 'Ọ̀', 'Ọ́', 'Ù', 'Ú']),
      scriptPrimary: /[A-Za-z\u00C0-\u00FF\u0100-\u024F]/g
    });

    this._distributions.set('zulu', {
      trigrams: new Map([
        ['nga', 0.0213], ['kwa', 0.0197], ['le ', 0.0187], ['la ', 0.0176],
        ['uku', 0.0165], ['nge', 0.0154], ['esi', 0.0143], ['wa ', 0.0132],
        ['ma ', 0.0121], ['aba', 0.0110], ['nd', 0.0106], ['ndi', 0.0102],
        ['di ', 0.0098], ['isi', 0.0094], ['siz', 0.0090], ['izu', 0.0086],
        ['zul', 0.0082], ['ulu', 0.0078], ['lu ', 0.0074], ['u u', 0.0070],
        ['uli', 0.0066], ['lim', 0.0062], ['imi', 0.0058], ['mi ', 0.0054],
        ['ol', 0.0050], ['olu', 0.0046], ['lun', 0.0042], ['ung', 0.0038],
        ['ngi', 0.0034], ['gil', 0.0030]
      ]),
      uniqueChars: new Set([]),
      scriptPrimary: /[A-Za-z]/g
    });

    // UNSUPPORTED LANGUAGES
    this._distributions.set('chechen', {
      trigrams: new Map([
        ['ан', 0.0287], ['ара', 0.0243], ['ла', 0.0217], ['на', 0.0184],
        ['ам', 0.0169], ['хь', 0.0161], ['ца', 0.0154], ['ра', 0.0147],
        ['къ', 0.0139], ['аь', 0.0134], ['ал', 0.0129], ['да', 0.0125],
        ['ва', 0.0121], ['чу', 0.0117], ['ха', 0.0113], ['ша', 0.0109],
        ['ду', 0.0106], ['ма', 0.0103], ['ч1', 0.0099], ['ун', 0.0096],
        ['ар', 0.0093], ['ат', 0.0089], ['хи', 0.0086], ['ол', 0.0083],
        ['ди', 0.0080], ['ур', 0.0077], ['ин', 0.0074], ['х1', 0.0071],
        ['ийн', 0.0068], ['аш', 0.0065]
      ]),
      uniqueChars: new Set(['ӏ', 'ъ', 'ь', 'э', 'ю', 'я', 'ё', 'ж', 'х', 'ч', 'ш', 'щ', 'ц']),
      scriptPrimary: /[А-Яа-яӀӏЁё]/g
    });

    this._distributions.set('chuvash', {
      trigrams: new Map([
        ['ен', 0.0298], ['ра', 0.0265], ['ĕн', 0.0242], ['та', 0.0229],
        ['ах', 0.0214], ['ан', 0.0203], ['не', 0.0192], ['ре', 0.0183],
        ['пĕ', 0.0172], ['ат', 0.0161], ['тă', 0.0154], ['ăн', 0.0149],
        ['че', 0.0143], ['ри', 0.0137], ['ма', 0.0131], ['сă', 0.0126],
        ['ва', 0.0121], ['ка', 0.0116], ['ăр', 0.0112], ['ер', 0.0108],
        ['пу', 0.0104], ['ла', 0.0100], ['ту', 0.0096], ['ун', 0.0092],
        ['ха', 0.0088], ['ме', 0.0085], ['ир', 0.0081], ['ăс', 0.0078],
        ['па', 0.0075], ['ул', 0.0072]
      ]),
      uniqueChars: new Set(['ă', 'ĕ', 'ç', 'ÿ', 'ӑ', 'ӗ', 'ҫ', 'ӳ']),
      scriptPrimary: /[A-Za-zĂăĔĕÇçӐӑӖӗҪҫӲӳ]/g
    });

    this._distributions.set('guarani', {
      trigrams: new Map([
        ['ha\'', 0.0276], ['pe ', 0.0249], ['ava', 0.0231], ['ã  ', 0.0218],
        ['nde', 0.0205], ['ete', 0.0192], ['che', 0.0182], ['por', 0.0171],
        ['gua', 0.0163], ['ara', 0.0155], ['rei', 0.0147], ['ñe\'', 0.0140],
        ['ite', 0.0134], ['ha ', 0.0128], ['ña ', 0.0122], ['mba', 0.0117],
        ['iko', 0.0112], ['cha', 0.0107], ['ype', 0.0102], ['ỹ  ', 0.0098],
        ['iru', 0.0094], ['py\'', 0.0090], ['jap', 0.0086], ['ñan', 0.0082],
        ['yva', 0.0079], ['oĩ ', 0.0076], ['nte', 0.0073], ['gue', 0.0070],
        ['ati', 0.0067], ['upi', 0.0064]
      ]),
      uniqueChars: new Set(['ã', 'ẽ', 'ĩ', 'õ', 'ũ', 'ỹ', 'ñ', 'ĝ', 'ŷ', 'ä', 'ë', 'ï', 'ö', 'ü', '\'']),
      scriptPrimary: /[A-Za-zÃãẼẽĨĩÕõŨũỸỹÑñĜĝŶŷÄäËëÏïÖöÜü]/g
    });

    this._distributions.set('inuktitut', {
      trigrams: new Map([
        ['ᐃᑦ', 0.0284], ['ᓂᑦ', 0.0261], ['ᑦᑕ', 0.0246], ['ᐊᓂ', 0.0231],
        ['ᐃᓕ', 0.0218], ['ᓗᒃ', 0.0204], ['ᒪᑦ', 0.0194], ['ᓄᑦ', 0.0183],
        ['ᓂᒃ', 0.0173], ['ᑕᑦ', 0.0163], ['ᐅᑦ', 0.0153], ['ᓇᑦ', 0.0145],
        ['ᐃᑉ', 0.0137], ['ᑯᑦ', 0.0130], ['ᑐᑦ', 0.0124], ['ᓯᓂ', 0.0118],
        ['ᕗᑦ', 0.0112], ['ᒥᑦ', 0.0107], ['ᓕᑦ', 0.0102], ['ᓂᐊ', 0.0097],
        ['ᑎᑦ', 0.0092], ['ᕐᒥ', 0.0088], ['ᒪᓂ', 0.0084], ['ᓂᑯ', 0.0080],
        ['ᒧᑦ', 0.0076], ['ᓱᓂ', 0.0073], ['ᒋᔭ', 0.0070], ['ᕐᓂ', 0.0067],
        ['ᓄᓇ', 0.0064], ['ᐊᓗ', 0.0061]
      ]),
      uniqueChars: new Set(['ᐁ', 'ᐃ', 'ᐅ', 'ᐊ', 'ᐋ', 'ᐱ', 'ᐳ', 'ᐸ', 'ᑎ', 'ᑐ', 'ᑕ', 'ᑫ', 'ᑭ', 'ᑯ', 'ᑲ']),
      scriptPrimary: /[\u1400-\u167F]/g
    });

    this._distributions.set('kalaallisut', {
      trigrams: new Map([
        ['it ', 0.0272], ['arpo', 0.0248], ['nga', 0.0232], ['ani', 0.0219],
        ['erp', 0.0204], ['sut', 0.0189], ['alu', 0.0176], ['unn', 0.0164],
        ['ssa', 0.0156], ['aat', 0.0149], ['ill', 0.0142], ['isu', 0.0135],
        ['una', 0.0128], ['mik', 0.0122], ['arp', 0.0116], ['put', 0.0110],
        ['nng', 0.0105], ['tut', 0.0100], ['ner', 0.0096], ['oqa', 0.0092],
        ['llu', 0.0088], ['gut', 0.0084], ['ass', 0.0080], ['nni', 0.0076],
        ['aan', 0.0073], ['mit', 0.0070], ['kku', 0.0067], ['lli', 0.0064],
        ['lak', 0.0061], ['taa', 0.0058]
      ]),
      uniqueChars: new Set(['ĸ', 'ĩ', 'ũ', 'ã', 'ẽ', 'õ']),
      scriptPrimary: /[A-Za-zĸĩũãẽõ]/g
    });

    this._distributions.set('mapudungun', {
      trigrams: new Map([
        ['che', 0.0283], ['pu ', 0.0256], ['mew', 0.0237], ['nga', 0.0223],
        ['wen', 0.0208], ['chi', 0.0193], ['kiñ', 0.0178], ['ñi ', 0.0170],
        ['che', 0.0162], ['we ', 0.0155], ['nge', 0.0148], ['egu', 0.0141],
        ['enw', 0.0134], ['kay', 0.0128], ['üym', 0.0122], ['kom', 0.0116],
        ['yin', 0.0110], ['imi', 0.0105], ['ka ', 0.0100], ['ül ', 0.0096],
        ['may', 0.0092], ['wün', 0.0088], ['fey', 0.0084], ['tüf', 0.0080],
        ['afi', 0.0076], ['iñc', 0.0073], ['tro', 0.0070], ['mün', 0.0067],
        ['rke', 0.0064], ['kül', 0.0061]
      ]),
      uniqueChars: new Set(['ü', 'ñ', 'ï', 'ë', 'ö', 'ä', 'þ', 'ø']),
      scriptPrimary: /[A-Za-zÜüÑñÏïËëÖöÄäÞþØø]/g
    });

    this._distributions.set('nahuatl', {
      trigrams: new Map([
        ['tli', 0.0272], ['tla', 0.0248], ['pan', 0.0231], ['chi', 0.0216],
        ['hua', 0.0202], ['hui', 0.0192], ['meh', 0.0182], ['tec', 0.0172],
        ['ca ', 0.0163], ['non', 0.0154], ['toc', 0.0146], ['ini', 0.0139],
        ['ayo', 0.0132], ['pal', 0.0125], ['mic', 0.0119], ['atl', 0.0113],
        ['qua', 0.0107], ['tzo', 0.0102], ['ehu', 0.0097], ['yan', 0.0092],
        ['iuh', 0.0088], ['tih', 0.0084], ['tlā', 0.0080], ['cue', 0.0076],
        ['och', 0.0072], ['poł', 0.0069], ['cal', 0.0066], ['cen', 0.0063],
        ['tēn', 0.0060], ['tzā', 0.0057]
      ]),
      uniqueChars: new Set(['ā', 'ē', 'ī', 'ō', 'ū', 'ł', 'ţ', 'ñ', 'tz', 'tl']),
      scriptPrimary: /[A-Za-zĀāĒēĪīŌōŪūŁłŢţÑñ]/g
    });

    this._distributions.set('navajo', {
      trigrams: new Map([
        ['íní', 0.0259], ['áád', 0.0235], ['haa', 0.0218], ['ání', 0.0205],
        ['nii', 0.0193], ['shí', 0.0182], ['ígí', 0.0171], ['éí ', 0.0161],
        ['doo', 0.0152], ['łah', 0.0143], ['yah', 0.0135], ['bee', 0.0128],
        ['gó ', 0.0121], ['zhi', 0.0115], ['ká ', 0.0109], ['ání', 0.0103],
        ['atł', 0.0098], ['adi', 0.0093], ['ích', 0.0088], ['ǫ\'í', 0.0084],
        ['chí', 0.0080], ['een', 0.0076], ['haz', 0.0072], ['yéé', 0.0068],
        ['naa', 0.0065], ['hǫ́', 0.0062], ['t\'é', 0.0059], ['ʼá ', 0.0056],
        ['tsé', 0.0053], ['din', 0.0050]
      ]),
      uniqueChars: new Set(['á', 'é', 'í', 'ó', 'ú', 'ą', 'ę', 'į', 'ǫ', 'ł', 'ʼ', '\'']),
      scriptPrimary: /[A-Za-zÁáÉéÍíÓóÚúĄąĘęĮįǪǫŁł'ʼ]/g
    });

    this._distributions.set('occitan', {
      trigrams: new Map([
        ['que', 0.0267], ['los', 0.0243], ['las', 0.0228], ['ent', 0.0213],
        ['tra', 0.0199], ['per', 0.0186], ['ian', 0.0174], ['del', 0.0163],
        ['als', 0.0153], ['ont', 0.0145], ['una', 0.0137], ['era', 0.0129],
        ['con', 0.0122], ['eri', 0.0116], ['res', 0.0110], ['ata', 0.0104],
        ['nh ', 0.0099], ['lh ', 0.0094], ['ça ', 0.0089], ['çon', 0.0085],
        ['osq', 0.0081], ['sar', 0.0077], ['eth', 0.0073], ['tau', 0.0070],
        ['ans', 0.0067], ['aci', 0.0064], ['ona', 0.0061], ['men', 0.0058],
        ['ira', 0.0055], ['gen', 0.0052],
        // Adding more distinctive Occitan trigrams
        ['lha', 0.0048], ['nha', 0.0045], ['eth', 0.0042], ['òla', 0.0039],
        ['vai', 0.0036], ['mai', 0.0033], ['au ', 0.0030], ['oc ', 0.0027]
      ]),
      // Refined unique character set removing duplicates and focusing on distinctive features
      uniqueChars: new Set(['à', 'á', 'â', 'ç', 'è', 'é', 'ê', 'ë', 'í', 'ï', 'ń', 'ò', 'ó', 'ô', 'ú', 'ù', 'ü', 'ñ']),
      scriptPrimary: /[A-Za-zÀàÁáÂâÇçÈèÉéÊêËëÍíÏïŃńÒòÓóÔôÚúÙùÜüÑñ]/g
    });

    this._distributions.set('quechua', {
      trigrams: new Map([
        ['kuna', 0.0284], ['ata ', 0.0257], ['nku ', 0.0239], ['aq  ', 0.0225],
        ['paq ', 0.0211], ['una ', 0.0197], ['p\'a', 0.0185], ['cha ', 0.0174],
        ['qan ', 0.0164], ['unku', 0.0154], ['man ', 0.0145], ['anta', 0.0137],
        ['inku', 0.0129], ['kay ', 0.0122], ['qpa ', 0.0115], ['hina', 0.0109],
        ['wan ', 0.0103], ['ta  ', 0.0097], ['chka', 0.0092], ['anku', 0.0087],
        ['nki ', 0.0082], ['spa ', 0.0078], ['ymá ', 0.0074], ['llan', 0.0070],
        ['qta ', 0.0066], ['qan ', 0.0063], ['ich ', 0.0060], ['kta ', 0.0057],
        ['nin ', 0.0054], ['puni', 0.0051]
      ]),
      uniqueChars: new Set(['\'', 'č', 'ĉ', 'ñ', 'š', 'ŝ', 'ł', 'ä', 'ü', 'ch', 'k\'', 'p\'', 'q\'', 't\'']),
      scriptPrimary: /[A-Za-z'ČčĉÑñŠšŝŁłÄäÜü]/g
    });

    this._distributions.set('romansh', {
      trigrams: new Map([
        ['cha', 0.0289], ['sch', 0.0264], ['ils', 0.0248], ['las', 0.0232],
        ['che', 0.0218], ['per', 0.0204], ['da ', 0.0191], ['ent', 0.0179],
        ['ins', 0.0168], ['tg ', 0.0158], ['cun', 0.0148], ['ans', 0.0139],
        ['la ', 0.0131], ['ina', 0.0123], ['ier', 0.0116], ['gia', 0.0109],
        ['una', 0.0103], ['era', 0.0097], ['tar', 0.0092], ['tra', 0.0087],
        ['ssa', 0.0082], ['tut', 0.0078], ['pli', 0.0074], ['tar', 0.0070],
        ['sco', 0.0066], ['ün ', 0.0063], ['als', 0.0060], ['our', 0.0057],
        ['ill', 0.0054], ['ils', 0.0051]
      ]),
      uniqueChars: new Set(['à', 'á', 'â', 'è', 'é', 'ê', 'ë', 'ì', 'í', 'î', 'ï', 'ò', 'ó', 'ô', 'ù', 'ú', 'û', 'ü', 'ÿ']),
      scriptPrimary: /[A-Za-zÀàÁáÂâÈèÉéÊêËëÌìÍíÎîÏïÒòÓóÔôÙùÚúÛûÜüŸÿ]/g
    });

    this._distributions.set('samoan', {
      trigrams: new Map([
        ['mai', 0.0277], ['ina', 0.0253], ['ana', 0.0236], ['le ', 0.0220],
        ['tou', 0.0204], ['ma ', 0.0189], ['atu', 0.0176], ['o l', 0.0163],
        ['ia ', 0.0151], ['fia', 0.0140], ['fa\'', 0.0131], ['lau', 0.0122],
        ['au ', 0.0114], ['sau', 0.0106], ['\'a ', 0.0099], ['i l', 0.0092],
        ['lou', 0.0086], ['ae ', 0.0080], ['mei', 0.0075], ['ga ', 0.0070],
        ['alo', 0.0066], ['aga', 0.0062], ['tua', 0.0058], ['\'o ', 0.0054],
        ['ua ', 0.0051], ['fai', 0.0048], ['oa ', 0.0045], ['ai ', 0.0042],
        ['mea', 0.0039], ['tou', 0.0037]
      ]),
      uniqueChars: new Set(['ā', 'ē', 'ī', 'ō', 'ū', '\'']),
      scriptPrimary: /[A-Za-zĀāĒēĪīŌōŪū]/g
    });

    this._distributions.set('sardinian', {
      trigrams: new Map([
        ['de ', 0.0268], ['sa ', 0.0242], ['su ', 0.0225], ['chi', 0.0209],
        ['tzi', 0.0194], ['eru', 0.0181], ['ent', 0.0169], ['con', 0.0158],
        ['pro', 0.0147], ['ta ', 0.0138], ['ist', 0.0129], ['unu', 0.0120],
        ['pre', 0.0113], ['per', 0.0106], ['ras', 0.0099], ['ala', 0.0093],
        ['ina', 0.0087], ['adu', 0.0082], ['iai', 0.0077], ['ana', 0.0072],
        ['ata', 0.0068], ['are', 0.0064], ['nti', 0.0060], ['rei', 0.0057],
        ['ssu', 0.0054], ['ddu', 0.0051], ['ant', 0.0048], ['tti', 0.0045],
        ['ere', 0.0043], ['iss', 0.0041]
      ]),
      uniqueChars: new Set(['è', 'é', 'ò', 'ó', 'ù', 'ú', 'ç', 'ñ', 'dd', 'tz', 'zz']),
      scriptPrimary: /[A-Za-zÈèÉéÒòÓóÙùÚúÇçÑñ]/g
    });

    this._distributions.set('sindhi', {
      trigrams: new Map([
        ['جي ', 0.0272], ['ان ', 0.0247], ['ين ', 0.0231], ['کي ', 0.0217],
        ['سند', 0.0203], ['ڪري', 0.0191], ['ندي', 0.0179], ['جو ', 0.0168],
        ['مان', 0.0158], ['هن ', 0.0148], ['سان', 0.0139], ['اهي', 0.0131],
        ['آهي', 0.0124], ['ائي', 0.0117], ['رکي', 0.0110], ['ٿي ', 0.0104],
        ['تي ', 0.0098], ['وار', 0.0093], ['سي ', 0.0088], ['هي ', 0.0083],
        ['وڏي', 0.0079], ['اري', 0.0075], ['ڪو ', 0.0071], ['ڪي ', 0.0067],
        ['ڏي ', 0.0064], ['وئي', 0.0061], ['يل ', 0.0058], ['ون ', 0.0055],
        ['هڙي', 0.0052], ['يون', 0.0049]
      ]),
      uniqueChars: new Set(['ڀ', 'ٻ', 'ڃ', 'چ', 'ڇ', 'ڊ', 'ڌ', 'ڍ', 'ڏ', 'ڊ', 'ڙ', 'ڪ', 'ڳ', 'ڱ', 'ڻ', '۽']),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F]/g
    });

    this._distributions.set('tahitian', {
      trigrams: new Map([
        ['te ', 0.0294], ['ia ', 0.0267], ['ata', 0.0249], ['no ', 0.0231],
        ['ra\'', 0.0214], ['\'a ', 0.0199], ['mai', 0.0186], ['tai', 0.0172],
        ['are', 0.0160], ['hi\'', 0.0148], ['to\'', 0.0138], ['hia', 0.0129],
        ['ore', 0.0120], ['tau', 0.0113], ['i t', 0.0106], ['e t', 0.0099],
        ['tah', 0.0093], ['ari', 0.0087], ['ana', 0.0082], ['i\'a', 0.0077],
        ['noa', 0.0072], ['ino', 0.0068], ['ei ', 0.0064], ['ea ', 0.0060],
        ['o t', 0.0057], ['roa', 0.0054], ['fa\'', 0.0051], ['mau', 0.0048],
        ['\'o ', 0.0045], ['oa ', 0.0043]
      ]),
      uniqueChars: new Set(['ā', 'ē', 'ī', 'ō', 'ū', '\'']),
      scriptPrimary: /[A-Za-zĀāĒēĪīŌōŪū]/g
    });

    this._distributions.set('tswana', {
      trigrams: new Map([
        ['ng ', 0.0279], ['go ', 0.0253], ['lo ', 0.0235], ['tla', 0.0218],
        ['mo ', 0.0202], ['tse', 0.0188], ['la ', 0.0174], ['tle', 0.0162],
        ['eng', 0.0151], ['o t', 0.0141], ['ang', 0.0132], ['ana', 0.0124],
        ['elo', 0.0117], ['ong', 0.0110], ['e t', 0.0103], ['ka ', 0.0096],
        ['wa ', 0.0091], ['sa ', 0.0085], ['a g', 0.0080], ['aga', 0.0075],
        ['ba ', 0.0071], ['ga ', 0.0067], ['tsa', 0.0063], ['e m', 0.0060],
        ['e k', 0.0057], ['rê ', 0.0054], ['di ', 0.0051], ['nts', 0.0048],
        ['a b', 0.0045], ['o m', 0.0043]
      ]),
      uniqueChars: new Set(['ê', 'ô', 'š', 'è', 'ò', 'ñ', 'ph', 'th', 'tl', 'ts']),
      scriptPrimary: /[A-Za-zÊêÔôŠšÈèÒòÑñ]/g
    });

    this._distributions.set('uygur', {
      trigrams: new Map([
        ['لار', 0.0267], ['نىڭ', 0.0243], ['ىنى', 0.0229], ['غان', 0.0214],
        ['قان', 0.0201], ['ىدا', 0.0188], ['مۇس', 0.0176], ['ۇلم', 0.0164],
        ['ىلى', 0.0154], ['قىل', 0.0145], ['لىق', 0.0136], ['ىدە', 0.0128],
        ['ەتت', 0.0120], ['دىن', 0.0113], ['ەن ', 0.0106], ['بىل', 0.0100],
        ['بار', 0.0094], ['ۇيغ', 0.0089], ['ۈن ', 0.0084], ['ىش ', 0.0079],
        ['تىل', 0.0075], ['لىك', 0.0071], ['ەر ', 0.0067], ['شۇ ', 0.0063],
        ['بول', 0.0060], ['گەن', 0.0057], ['ىڭ ', 0.0054], ['ۇر ', 0.0051],
        ['ايد', 0.0048], ['ەمم', 0.0045]
      ]),
      uniqueChars: new Set(['ئ', 'ا', 'ە', 'ب', 'پ', 'ت', 'ج', 'چ', 'خ', 'د', 'ر', 'ز', 'ژ', 'س', 'ش', 'غ', 'ف', 'ق', 'ك', 'گ', 'ڭ', 'ل', 'م', 'ن', 'ھ', 'و', 'ۇ', 'ۆ', 'ۈ', 'ې', 'ى', 'ي']),
      scriptPrimary: /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g
    });

    this._distributions.set('uzbek', {
      trigrams: new Map([
        ['lar', 0.0289], ['da ', 0.0264], ['an ', 0.0241], ['ing', 0.0227],
        ['ga ', 0.0213], ['shi', 0.0199], ['ish', 0.0186], ['ni ', 0.0174],
        ['dan', 0.0163], ['ar ', 0.0153], ['dir', 0.0144], ['ida', 0.0135],
        ['gan', 0.0127], ['adi', 0.0120], ['ila', 0.0113], ['bir', 0.0107],
        ['lik', 0.0101], ['cha', 0.0095], ['ili', 0.0090], ['olg', 0.0085],
        ['on ', 0.0081], ['in ', 0.0077], ['lan', 0.0073], ['ich', 0.0069],
        ['at ', 0.0066], ['kun', 0.0063], ['hi ', 0.0060], ['ari', 0.0057],
        ['nda', 0.0054], ['as ', 0.0051]
      ]),
      uniqueChars: new Set(['ʻ', 'ʼ', 'ʽ', '\'', 'ʾ', 'ʿ', 'о', 'о́', 'ғ', 'қ', 'ў', 'ҳ', 'ö', 'ğ', 'ş', 'ç']),
      scriptPrimary: /[A-Za-zʻʼʽ'ʾʿЁёА-яҒғҚқЎўҲҳÖöĞğŞşÇç]/g
    });

    this._distributions.set('wolof', {
      trigrams: new Map([
        ['na ', 0.0272], ['ak ', 0.0254], ['am ', 0.0237], ['oom', 0.0221],
        ['la ', 0.0206], ['ngi', 0.0192], ['aan', 0.0179], ['een', 0.0167],
        ['nga', 0.0156], ['lu ', 0.0146], ['wi ', 0.0137], ['si ', 0.0128],
        ['gi ', 0.0120], ['ni ', 0.0113], ['kat', 0.0106], ['ñu ', 0.0100],
        ['u n', 0.0094], ['bi ', 0.0089], ['i m', 0.0084], ['ji ', 0.0079],
        ['naa', 0.0075], ['enn', 0.0071], ['ci ', 0.0067], ['aat', 0.0064],
        ['oor', 0.0061], ['eek', 0.0058], ['ël ', 0.0055], ['añ ', 0.0052],
        ['ëgg', 0.0049], ['ëpp', 0.0047]
      ]),
      uniqueChars: new Set(['ë', 'é', 'à', 'è', 'ù', 'ñ', 'ŋ', 'ń', 'ó', 'á', 'ú', 'í']),
      scriptPrimary: /[A-Za-zËëÉéÀàÈèÙùÑñŊŋŃńÓóÁáÚúÍí]/g
    });
  }

  /**
   * Get language distribution by language name
   * @param language Language name (e.g., "english")
   * @returns Language distribution or undefined if not found
   */
  static getDistribution(language: string): LanguageDistribution | undefined {
    return this._distributions.get(language);
  }

  /**
   * Get all supported language names
   * @returns Array of language names
   */
  static getLanguages(): string[] {
    return Array.from(this._distributions.keys());
  }

  /**
   * Get all language distributions
   * @returns Map of language distributions
   */
  static getAllDistributions(): Map<string, LanguageDistribution> {
    return new Map(this._distributions);
  }
}

// Initialize language distributions when the module is loaded
LanguageDistributions.initialize();

/**
 * Returns all language distributions
 * Legacy compatibility function to maintain backwards compatibility
 */
export function getLanguageDistributions(): Record<string, LanguageDistribution> {
  const result: Record<string, LanguageDistribution> = {};

  for (const [language, distribution] of LanguageDistributions.getAllDistributions()) {
    result[language] = distribution;
  }

  return result;
}
